import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, ClipboardList, LucideAngularModule, Pencil, PlusCircle, Send, Trash2, X } from 'lucide-angular';
import { Charge, Contract } from '../core/models/domain.models';
import { ApiService } from '../core/services/api.service';
import { ContractsService } from '../features/contract/services/contracts.service';
import { Vendor } from '../core/models/domain.models';
import { Loa, LoaBillConfig, LoaSchedule } from '../core/models/domain.models';
import { VendorsService } from '../features/vendor/services/vendors.service';
import { LoaService } from '../features/loa/services/loa.service';
import { ChargesService } from '../features/charges/services/charges.service';
import { ToastService } from '../shared/ui/toast.service';
import { AuthService } from '../core/services/auth.service';
import { type ContractAnalysisReportRow } from '../features/contract/services/contracts.service';
import {
  buildContractChargeBreakdown,
  type ContractChargeBreakdownRow,
  type ContractChargeBreakdownSummary,
  type ContractChargePartyLike,
} from '../features/contract/utils/contract-profit.util';

interface PartyRow {
  id: number;
  clientId: string;
  clientName: string;
  entityType: string;
  loaId: string;
  clientLoi: string;
  scheduleAgreementId: string;
  loiType: string;
  agreementType: string;
  linkLoi: string;
  rldc?: string | null;
  quantum: string;
  quantum_mwh: string;
  usedQuantum: string;
}

interface CompatibilityRow {
  parameter: string;
  sourceValue: string;
  targetValue: string;
  status: 'MATCH' | 'MISMATCH';
}

interface ScheduleOption {
  id: string;
  label: string;
  quantum: string;
}

interface OaChargeAllocation {
  charge_type: string;
  payer: 'Buyer' | 'Seller' | 'Shared';
  buyer_percent: number;
  seller_percent: number;
}

interface ApplicationChargeApi {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
}

interface ApplicationApi {
  id: string;
  application_number?: string | null;
  contract_id?: string | null;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  schedules?: Array<{
    from_date: string;
    to_date: string;
    from_time: string;
    to_time: string;
    quantum?: number | string | null;
  }>;
  approval_charges?: ApplicationChargeApi[];
}

interface EstimatedProfitRow {
  rowId: number;
  clientName: string;
  loaNumber: string;
  loiType: string;
  quantum: number;
  rate: number | null;
  amount: number;
  direction: 'revenue' | 'cost' | 'excluded' | 'unknown';
  rateSource: string;
  quantumLabel?: string;
  quantumTooltip?: string;
  rateLabel?: string;
}

type ChargeBreakdownRow = ContractChargeBreakdownRow;
type ChargeBreakdownSummary = ContractChargeBreakdownSummary;

interface ChargeBreakdownModal {
  title: string;
  subtitle: string;
  equation: string;
  hint: string;
  details: Array<{ label: string; value: string }>;
}

type Mode = 'new' | 'edit' | 'view';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  styles: [
    `
      .contract-form-shell {
        background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
        border: 1px solid #dbe7ff;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(33, 76, 170, 0.08);
        min-height: calc(100vh - 110px);
        font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      }
      .form-header {
        padding: 18px 22px 6px;
      }
      .form-title {
        margin: 0;
        font-size: 2rem;
        font-weight: 700;
        letter-spacing: -0.03em;
        color: #24334f;
      }
      .title-badge {
        width: 42px;
        height: 42px;
        border-radius: 14px;
        background: linear-gradient(135deg, #eef4ff 0%, #dbe8ff 100%);
        color: #3d66da;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: inset 0 0 0 1px rgba(61, 102, 218, 0.14);
      }
      .btn-back {
        background: #ffffff;
        border: 1px solid #d3dcef;
        color: #3557af;
        font-weight: 600;
        min-width: 96px;
        border-radius: 999px;
        height: 40px;
      }
      .content-wrap {
        padding: 14px 22px 24px;
      }
      .contract-no-row {
        padding: 0 0 18px;
      }
      .contract-no-label {
        font-size: 14px;
        font-weight: 700;
        color: #4d5b74;
        margin-right: 10px;
      }
      .contract-no-input {
        width: 230px;
        height: 40px;
        border: 1px solid #d1dbec;
        border-radius: 12px;
        background: #ffffff;
        padding: 8px 12px;
        color: #2a3a50;
      }
      .linked-loa-alert {
        margin: 0 0 18px;
        border: 1px solid #ffe0a3;
        border-radius: 14px;
        background: #fff8e8;
        color: #8a5a00;
        padding: 12px 14px;
        font-size: 14px;
        font-weight: 600;
      }
      .party-card {
        border: 1px solid #d8e2f3;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 8px 18px rgba(47, 88, 177, 0.08);
      }
      .party-head {
        background: linear-gradient(180deg, #f9fbff 0%, #edf3ff 100%);
        border-bottom: 1px solid #dbe5f6;
        border-radius: 18px 18px 0 0;
        padding: 14px 16px;
      }
      .party-title {
        margin: 0;
        font-size: 1.6rem;
        font-weight: 700;
        color: #24334f;
      }
      .btn-add-row {
        background: linear-gradient(135deg, #0f5fd3 0%, #1e73e8 100%);
        border-color: #0f5fd3;
        color: #fff;
        font-weight: 700;
        min-width: 98px;
        border-radius: 999px;
        box-shadow: 0 10px 18px rgba(15, 95, 211, 0.18);
      }
      .party-table-wrap {
        padding: 14px 16px 16px;
      }
      .party-table-shell {
        border: 1px solid #dfe7f5;
        border-radius: 16px;
        overflow: auto;
        background: #ffffff;
      }
      .party-table {
        min-width: 1300px;
        margin: 0;
      }
      .party-table th {
        background: #eef2f7;
        border-bottom: 1px solid #d8e2f3 !important;
        text-transform: uppercase;
        letter-spacing: 0;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
      }
      .party-table td {
        border-bottom: 1px solid #e7edf7 !important;
        background: #ffffff;
        height: 42px;
        font-size: 13px;
      }
      .row-action-btn {
        width: 30px;
        height: 30px;
        border: 1px solid #d8e2f3;
        border-radius: 8px;
        background: #f8fbff;
        color: #6d7685;
      }
      .compat-card {
        border: 1px solid #d8e2f3;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 8px 18px rgba(47, 88, 177, 0.08);
      }
      .compat-head {
        background: linear-gradient(180deg, #f9fbff 0%, #edf3ff 100%);
        border-bottom: 1px solid #dbe5f6;
        border-radius: 18px 18px 0 0;
        padding: 14px 16px;
      }
      .compat-title {
        margin: 0;
        font-size: 1.45rem;
        font-weight: 700;
        color: #24334f;
      }
      .compat-subtitle {
        margin: 0;
        font-size: 14px;
        color: #6b7892;
      }
      .btn-close-compat {
        min-width: 70px;
        background: #0d54ba;
        border-color: #0d54ba;
        color: #fff;
        font-weight: 700;
      }
      .compat-body {
        padding: 14px 16px 16px;
      }
      .compat-table-shell {
        border: 1px solid #dfe7f5;
        border-radius: 16px;
        overflow: auto;
        background: #ffffff;
      }
      .compat-table {
        min-width: 760px;
        margin: 0;
      }
      .compat-table th {
        background: #eef2f7;
        border-bottom: 1px solid #d8e2f3 !important;
        font-size: 12px;
        font-weight: 700;
      }
      .compat-table td {
        border-bottom: 1px solid #e7edf7 !important;
        background: #ffffff;
        font-size: 13px;
      }
      .status-mismatch {
        color: #ad4d00;
        font-weight: 700;
      }
      .charge-card {
        border: 1px solid #d8e2f3;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 8px 18px rgba(47, 88, 177, 0.08);
        margin-top: 12px;
      }
      .charge-head {
        background: linear-gradient(180deg, #f9fbff 0%, #edf3ff 100%);
        border-bottom: 1px solid #dbe5f6;
        border-radius: 18px 18px 0 0;
        padding: 14px 16px;
      }
      .charge-title {
        margin: 0;
        font-size: 1.6rem;
        font-weight: 700;
        color: #24334f;
      }
      .charge-subtitle {
        margin: 0;
        font-size: 14px;
        color: #6b7892;
      }
      .charge-body {
        padding: 14px 16px 16px;
      }
      .charge-line {
        border: 1px solid #dfe7f5;
        border-radius: 14px;
        background: #f9fbff;
        padding: 10px 12px;
      }
      .charge-line + .charge-line {
        margin-top: 10px;
      }
      .charge-grid {
        display: grid;
        grid-template-columns: repeat(6, minmax(120px, 1fr));
        gap: 10px;
        margin-top: 10px;
      }
      .charge-key {
        color: #7b8594;
        font-weight: 700;
      }
      .charge-val {
        font-size: 18px;
        font-weight: 700;
        color: #24334f;
      }
      .profit-card {
        border: 1px solid #d8e2f3;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 8px 18px rgba(47, 88, 177, 0.08);
        margin-top: 12px;
      }
      .profit-head {
        background: linear-gradient(180deg, #f9fbff 0%, #edf3ff 100%);
        border-bottom: 1px solid #dbe5f6;
        border-radius: 18px 18px 0 0;
        padding: 14px 16px;
      }
      .profit-title {
        margin: 0;
        font-size: 1.6rem;
        font-weight: 700;
        color: #24334f;
      }
      .profit-subtitle {
        margin: 0;
        font-size: 14px;
        color: #6b7892;
      }
      .profit-body {
        padding: 14px 16px 16px;
      }
      .profit-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(160px, 1fr));
        gap: 12px;
        margin-bottom: 14px;
      }
      .profit-stat {
        border: 1px solid #dfe7f5;
        border-radius: 14px;
        background: #f9fbff;
        padding: 12px 14px;
      }
      .profit-stat-label {
        font-size: 12px;
        font-weight: 700;
        color: #6b7892;
        text-transform: uppercase;
      }
      .profit-stat-value {
        margin-top: 6px;
        font-size: 22px;
        font-weight: 700;
        color: #24334f;
      }
      .profit-stat-value.positive {
        color: #18804b;
      }
      .profit-stat-value.negative {
        color: #c0392b;
      }
      @media (max-width: 1200px) {
        .profit-grid {
          grid-template-columns: repeat(2, minmax(160px, 1fr));
        }
      }
      @media (max-width: 640px) {
        .profit-grid {
          grid-template-columns: minmax(160px, 1fr);
        }
      }
      .profit-table-shell {
        border: 1px solid #dfe7f5;
        border-radius: 16px;
        overflow: auto;
        background: #ffffff;
      }
      .profit-table {
        min-width: 760px;
        margin: 0;
      }
      .profit-table th {
        background: #eef2f7;
        border-bottom: 1px solid #d8e2f3 !important;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
      }
      .profit-table td {
        border-bottom: 1px solid #e7edf7 !important;
        background: #ffffff;
        font-size: 13px;
      }
      .suggested-app-circle {
        width: 22px;
        height: 22px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 800;
        background: rgba(13, 110, 253, 0.12);
        color: #0d6efd;
        border: 1px solid rgba(13, 110, 253, 0.25);
        flex: 0 0 auto;
      }
      .profit-direction {
        font-weight: 700;
      }
      .profit-direction.revenue {
        color: #18804b;
      }
      .profit-direction.cost {
        color: #c0392b;
      }
      .profit-direction.excluded,
      .profit-direction.unknown {
        color: #7b879c;
      }
      .action-row {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 16px;
      }
      .btn-cancel {
        background: #ffffff;
        border-color: #d1d9e8;
        color: #7b879c;
        font-weight: 700;
        min-width: 96px;
        border-radius: 12px;
        height: 42px;
      }
      .btn-submit {
        background: linear-gradient(135deg, #0f5fd3 0%, #1e73e8 100%);
        border-color: #0d54ba;
        color: #fff;
        font-weight: 700;
        min-width: 96px;
        border-radius: 12px;
        height: 42px;
        box-shadow: 0 10px 18px rgba(15, 95, 211, 0.18);
      }
      .modal-backdrop-lite {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.35);
        z-index: 1050;
      }
      .party-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(1140px, calc(100vw - 32px));
        border: 1px solid #7082ad;
        border-radius: 18px;
        background: #f8fbff;
        z-index: 1060;
        box-shadow: 0 20px 48px rgba(15, 23, 42, 0.22);
      }
      .party-modal-head {
        background: linear-gradient(135deg, #3653d8 0%, #4b6cff 100%);
        color: #fff;
        padding: 14px 16px;
        border-radius: 18px 18px 0 0;
      }
      .party-modal-title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 700;
      }
      .btn-close-square {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        border: 0;
        background: #000;
        color: #fff;
      }
      .party-modal-body {
        padding: 16px 16px 14px;
      }
      .field-wrap {
        position: relative;
      }
      .field-label {
        position: absolute;
        top: -10px;
        left: 12px;
        padding: 0 4px;
        font-size: 12px;
        font-weight: 600;
        background: #f8fbff;
        color: #1b1b1b;
        z-index: 1;
      }
      .field-label .req {
        color: #db3b3b;
      }
      .modal-select {
        height: 40px;
        border-color: #d1dbec;
        border-radius: 12px;
        color: #5b677b;
        background: #ffffff;
      }
      .btn-party-add {
        min-width: 150px;
        background: linear-gradient(135deg, #0f5fd3 0%, #1e73e8 100%);
        border-color: #0d54ba;
        color: #fff;
        font-weight: 700;
        border-radius: 12px;
        height: 42px;
      }
      @media (max-width: 768px) {
        .form-header {
          padding: 16px 16px 6px;
        }
        .content-wrap {
          padding: 12px 16px 20px;
        }
        .contract-no-row {
          flex-direction: column;
          align-items: flex-start !important;
          gap: 8px;
        }
        .contract-no-input {
          width: 100%;
          max-width: 280px;
        }
      }
    `,
  ],
  template: `
    <div class="contract-form-shell">
      <div class="d-flex align-items-center justify-content-between form-header">
        <div class="d-flex align-items-center gap-2">
          <span class="title-badge">
            <lucide-icon [img]="ClipboardListIcon" [size]="20"></lucide-icon>
          </span>
          <h1 class="form-title">{{ pageTitle() }}</h1>
        </div>
        <button class="btn btn-back d-inline-flex align-items-center gap-2" type="button" (click)="router.navigateByUrl('/contracts')">
          <lucide-icon [img]="ArrowLeftIcon" [size]="16"></lucide-icon>
          <span>BACK</span>
        </button>
      </div>

      <div class="content-wrap">
        <div class="contract-no-row d-flex align-items-center">
          <span class="contract-no-label">Contract Number :</span>
          <input class="contract-no-input" [(ngModel)]="contractNumber" [readonly]="mode() === 'view'" />
        </div>

        <div class="row g-3">
          <div class="col-12" [class.col-xl-6]="showCompatibility()">
            <div class="party-card">
              <div class="party-head d-flex align-items-center justify-content-between">
                <h2 class="party-title">Party Details</h2>
                <button class="btn btn-add-row d-inline-flex align-items-center gap-2" type="button" [disabled]="!canModifyRows()" (click)="openAddModal()">
                  <lucide-icon [img]="PlusCircleIcon" [size]="16"></lucide-icon>
                  <span>ADD</span>
                </button>
              </div>

              <div class="party-table-wrap">
                <div class="party-table-shell">
                  <table class="table party-table mb-0">
                    <thead>
                      <tr>
                        <th>SR. NO</th>
                        <th>CLIENT NAME</th>
                        <th>CLIENT LOI</th>
                        <th>LOI TYPE</th>
                        <th>LINK LOI</th>
                        <th>QUANTUM</th>
                        <th>USED QUANTUM</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (rows().length === 0) {
                        <tr>
                          <td colspan="8"></td>
                        </tr>
                      }
                      @for (row of rows(); track row.id; let i = $index) {
                        <tr>
                          <td>{{ i + 1 }}</td>
                          <td>{{ row.clientName }}</td>
                          <td>{{ row.clientLoi }}</td>
                          <td>{{ row.loiType }}</td>
                          <td>{{ row.agreementType !== 'NORMAL' ? row.linkLoi : '-' }}</td>
                          <td>{{ row.quantum_mwh }} MWh</td>
                          <td>{{ row.usedQuantum }} MWh</td>
                          <td>
                            <button class="row-action-btn me-1" type="button" [disabled]="!canModifyRows()" (click)="openEditModal(row)"><lucide-icon [img]="PencilIcon" [size]="15"></lucide-icon></button>
                            <button class="row-action-btn" type="button" [disabled]="!canModifyRows()" (click)="removeRow(row.id)"><lucide-icon [img]="Trash2Icon" [size]="15"></lucide-icon></button>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          @if (showCompatibility()) {
            <div class="col-12 col-xl-6">
              <div class="compat-card">
                <div class="compat-head d-flex align-items-start justify-content-between">
                  <div>
                    <h2 class="compat-title">Compatibility Analysis</h2>
                    <p class="compat-subtitle">Validation of contract parameters against LOI</p>
                  </div>
                </div>
                <div class="compat-body">
                  <div class="compat-table-shell">
                    <table class="table compat-table mb-0">
                      <thead>
                        <tr>
                          <th>Parameter</th>
                          <th>{{ compatibilityHeaderSource() }}</th>
                          @if (hasCompatibilityTarget()) {
                            <th>{{ compatibilityHeaderTarget() }}</th>
                          }
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (item of compatibilityRows(); track item.parameter) {
                          <tr>
                            <td>{{ item.parameter }}</td>
                            <td>{{ item.sourceValue }}</td>
                            @if (hasCompatibilityTarget()) {
                              <td>{{ item.targetValue }}</td>
                            }
                            <td><span [class.status-mismatch]="item.status === 'MISMATCH'">{{ item.status }}</span></td>
                            <td><lucide-icon [img]="SendIcon" [size]="14"></lucide-icon></td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        @if (chargeAllocationBlocks().length > 0) {
          <div class="charge-card">
            <div class="charge-head">
              <h2 class="charge-title">Charge Allocation</h2>
              <p class="charge-subtitle">Open Access (OA) charge distribution between parties</p>
            </div>
            <div class="charge-body">
              @for (block of chargeAllocationBlocks(); track block.loaId) {
                <div class="charge-line">
                  <div class="charge-val">LOI: {{ block.loaTitle }}</div>
                  <div class="charge-grid">
                    @for (item of block.allocations; track item.charge_type) {
                      <div>
                        <div class="charge-key">{{ item.charge_type }}:</div>
                        <div class="fw-semibold">Buyer: {{ item.buyer_percent }}% | Seller: {{ item.seller_percent }}%</div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <div class="action-row">
          @if (mode() === 'view' && canEditCurrentContract()) {
            <button class="btn btn-submit" type="button" (click)="goToEdit()">EDIT</button>
          }
          @if (mode() === 'view' && canApproveCurrentContract()) {
            <button class="btn btn-submit" type="button" (click)="approveContract()">APPROVE</button>
            <button class="btn btn-cancel" type="button" (click)="rejectContract()">REJECT</button>
          }
          <button class="btn btn-cancel" type="button" (click)="router.navigateByUrl('/contracts')">CANCEL</button>
          @if (mode() !== 'view') {
            <button class="btn btn-submit" type="button" (click)="submitContract()">{{ mode() === 'edit' ? 'UPDATE' : 'SUBMIT' }}</button>
          }
        </div>

        @if (mode() === 'view' && estimatedProfitRows().length > 0) {
          <div class="row g-3">
            <div class="col-12 col-xl-6">
              <div class="profit-card h-100">
                <div class="profit-head">
                  <div>
                    <h2 class="profit-title">Estimated Profit</h2>
                    <p class="profit-subtitle">Based on selected LOAs, chosen schedule quantum, latest available LOA rate, and application_charges bifurcation</p>
                  </div>
                  @if (contractChargeBreakdown().rows.length > 0) {
                    <button class="btn btn-outline-primary btn-sm" type="button" (click)="openChargeBreakdownModal()">Charge Bifurcation</button>
                  }
                </div>
                <div class="profit-body">
                  <div class="profit-grid">
                    <div class="profit-stat">
                      <div class="profit-stat-label">Seller Cost</div>
                      <div class="profit-stat-value negative">{{ formatCurrency(estimatedSellerCost()) }}</div>
                    </div>
                    <div class="profit-stat">
                      <div class="profit-stat-label">Buyer Cost</div>
                      <div class="profit-stat-value positive">{{ formatCurrency(estimatedBuyerCost()) }}</div>
                    </div>
                    <div class="profit-stat">
                      <div class="profit-stat-label">Application Charges</div>
                      <div class="profit-stat-value negative">{{ formatCurrency(estimatedChargeCost()) }}</div>
                    </div>
                    <div class="profit-stat" [attr.title]="estimatedProfitTooltip()">
                      <div class="profit-stat-label">Net Estimated Profit</div>
                      <div class="profit-stat-value" [class.positive]="estimatedProfit() >= 0" [class.negative]="estimatedProfit() < 0">
                        {{ formatCurrency(estimatedProfit()) }}
                      </div>
                    </div>
                  </div>

                  @if (contractChargeBreakdown().rows.length > 0) {
                    <div class="profit-table-shell">
                      <table class="table profit-table mb-0">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Charges</th>
                            <th>Values</th>
                            <th>Buyer</th>
                            <th>Seller</th>
                            <th>Trader</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (charge of contractChargeBreakdown().rows; track charge.applicationNumber + '-' + charge.chargeType; let i = $index) {
                            <tr>
                              <td>{{ i + 1 }}</td>
                              <td>
                                <span class="d-inline-flex align-items-center gap-2">
                                  <span>{{ charge.chargeType }}{{ charge.rateType ? ' (' + charge.rateType + ')' : '' }}</span>
                                  @if (isApplicationFeeCharge(charge.chargeType) && suggestedApplicationMeta(); as meta) {
                                    <span
                                      class="suggested-app-circle"
                                      [attr.title]="suggestedApplicationTooltip()"
                                    >
                                      {{ meta.count }}
                                    </span>
                                  }
                                </span>
                              </td>
                              <td>{{ formatCurrency(charge.totalAmount) }}</td>
                              <td>{{ formatCurrency(charge.buyerAmount) }}</td>
                              <td>{{ formatCurrency(charge.sellerAmount) }}</td>
                              <td>{{ formatCurrency(charge.seciAmount) }}</td>
                            </tr>
                          }
                          <tr class="fw-semibold">
                            <td colspan="2">Total</td>
                            <td>{{ formatCurrency(contractChargeBreakdown().totalAmount) }}</td>
                            <td>{{ formatCurrency(contractChargeBreakdown().buyerAmount) }}</td>
                            <td>{{ formatCurrency(contractChargeBreakdown().sellerAmount) }}</td>
                            <td>{{ formatCurrency(contractChargeBreakdown().seciAmount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  }

                  <div class="profit-table-shell mt-3">
                    <table class="table profit-table mb-0">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>LOI</th>
                          <th>Type</th>
                          <th>Quantum</th>
                          <th>Rate</th>
                          <th>Rate Source</th>
                          <th>Contribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (item of estimatedProfitTableRows(); track item.rowId) {
                          <tr>
                            <td>{{ item.clientName }}</td>
                            <td>{{ item.loaNumber }}</td>
                            <td>{{ item.loiType }}</td>
                            <td [attr.title]="item.quantumTooltip || null">{{ item.quantumLabel || (formatNumber(item.quantum) + ' MWh') }}</td>
                            <td>{{ item.rateLabel || (item.rate === null ? 'NA' : formatCurrency(item.rate)) }}</td>
                            <td>{{ item.rateSource }}</td>
                            <td>
                              <span
                                class="profit-direction"
                                [class.revenue]="item.direction === 'revenue'"
                                [class.cost]="item.direction === 'cost'"
                                [class.excluded]="item.direction === 'excluded'"
                                [class.unknown]="item.direction === 'unknown'"
                              >
                                {{ formatEstimatedContribution(item) }}
                              </span>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-xl-6">
              <div class="profit-card h-100">
                <div class="profit-head">
                  <div>
                    <h2 class="profit-title">Actual Profit</h2>
                    <p class="profit-subtitle">Scaled by approved quantum from Contracts Analysis (approved ÷ requested)</p>
                  </div>
                  @if (actualChargeBreakdown().rows.length > 0) {
                    <button class="btn btn-outline-primary btn-sm" type="button" (click)="openActualChargeBreakdownModal()">Charge Bifurcation</button>
                  }
                </div>
                <div class="profit-body">
                  <div class="profit-grid">
                    <div class="profit-stat">
                      <div class="profit-stat-label">Seller Cost</div>
                      <div class="profit-stat-value negative">{{ formatCurrencyMaybe(actualCost()) }}</div>
                    </div>
                    <div class="profit-stat">
                      <div class="profit-stat-label">Buyer Cost</div>
                      <div class="profit-stat-value positive">{{ formatCurrencyMaybe(actualRevenue()) }}</div>
                    </div>
                    <div class="profit-stat">
                      <div class="profit-stat-label">Application Charges</div>
                      <div class="profit-stat-value negative">{{ formatCurrency(actualChargeCost()) }}</div>
                    </div>
                    <div class="profit-stat" [attr.title]="actualProfitTooltip()">
                      <div class="profit-stat-label">Net Actual Profit</div>
                      <div
                        class="profit-stat-value"
                        [class.positive]="isActualProfitPositive()"
                        [class.negative]="isActualProfitNegative()"
                      >
                        {{ formatCurrencyMaybe(actualProfit()) }}
                      </div>
                    </div>
                  </div>
                  @if (actualChargeBreakdown().rows.length > 0) {
                    <div class="profit-table-shell">
                      <table class="table profit-table mb-0">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Charges</th>
                            <th>Values</th>
                            <th>Buyer</th>
                            <th>Seller</th>
                            <th>Trader</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (charge of actualChargeBreakdown().rows; track charge.applicationNumber + '-' + charge.chargeType; let i = $index) {
                            <tr>
                              <td>{{ i + 1 }}</td>
                              <td>{{ charge.chargeType }}{{ charge.rateType ? ' (' + charge.rateType + ')' : '' }}</td>
                              <td>{{ formatCurrency(charge.totalAmount) }}</td>
                              <td>{{ formatCurrency(charge.buyerAmount) }}</td>
                              <td>{{ formatCurrency(charge.sellerAmount) }}</td>
                              <td>{{ formatCurrency(charge.seciAmount) }}</td>
                            </tr>
                          }
                          <tr class="fw-semibold">
                            <td colspan="2">Total</td>
                            <td>{{ formatCurrency(actualChargeBreakdown().totalAmount) }}</td>
                            <td>{{ formatCurrency(actualChargeBreakdown().buyerAmount) }}</td>
                            <td>{{ formatCurrency(actualChargeBreakdown().sellerAmount) }}</td>
                            <td>{{ formatCurrency(actualChargeBreakdown().seciAmount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  }

                  <div class="profit-table-shell mt-3">
                    <table class="table profit-table mb-0">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>LOI</th>
                          <th>Type</th>
                          <th>Quantum</th>
                          <th>Rate</th>
                          <th>Rate Source</th>
                          <th>Contribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (item of actualProfitTableRows(); track item.rowId) {
                          <tr>
                            <td>{{ item.clientName }}</td>
                            <td>{{ item.loaNumber }}</td>
                            <td>{{ item.loiType }}</td>
                            <td [attr.title]="item.quantumTooltip || null">{{ item.quantumLabel || (formatNumber(item.quantum) + ' MWh') }}</td>
                            <td>{{ item.rateLabel || (item.rate === null ? 'NA' : formatCurrency(item.rate)) }}</td>
                            <td>{{ item.rateSource }}</td>
                            <td>
                              <span
                                class="profit-direction"
                                [class.revenue]="item.direction === 'revenue'"
                                [class.cost]="item.direction === 'cost'"
                                [class.excluded]="item.direction === 'excluded'"
                                [class.unknown]="item.direction === 'unknown'"
                              >
                                {{ formatEstimatedContribution(item) }}
                              </span>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        } @else if (mode() !== 'view' && estimatedProfitRows().length > 0) {
          <div class="profit-card">
            <div class="profit-head">
              <div>
                <h2 class="profit-title">Estimated Profit</h2>
                <p class="profit-subtitle">Based on selected LOAs, chosen schedule quantum, latest available LOA rate, and application_charges bifurcation</p>
              </div>
              @if (contractChargeBreakdown().rows.length > 0) {
                <button class="btn btn-outline-primary btn-sm" type="button" (click)="openChargeBreakdownModal()">Charge Bifurcation</button>
              }
            </div>
            <div class="profit-body">
              <div class="profit-grid">
                <div class="profit-stat">
                  <div class="profit-stat-label">Seller Cost</div>
                  <div class="profit-stat-value negative">{{ formatCurrency(estimatedSellerCost()) }}</div>
                </div>
                <div class="profit-stat">
                  <div class="profit-stat-label">Buyer Cost</div>
                  <div class="profit-stat-value positive">{{ formatCurrency(estimatedBuyerCost()) }}</div>
                </div>
                <div class="profit-stat">
                  <div class="profit-stat-label">Application Charges</div>
                  <div class="profit-stat-value negative">{{ formatCurrency(estimatedChargeCost()) }}</div>
                </div>
                <div class="profit-stat" [attr.title]="estimatedProfitTooltip()">
                  <div class="profit-stat-label">Net Estimated Profit</div>
                  <div class="profit-stat-value" [class.positive]="estimatedProfit() >= 0" [class.negative]="estimatedProfit() < 0">
                    {{ formatCurrency(estimatedProfit()) }}
                  </div>
                </div>
              </div>

              @if (contractChargeBreakdown().rows.length > 0) {
                <div class="profit-table-shell">
                  <table class="table profit-table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Charges</th>
                        <th>Values</th>
                        <th>Buyer</th>
                        <th>Seller</th>
                        <th>Trader</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (charge of contractChargeBreakdown().rows; track charge.applicationNumber + '-' + charge.chargeType; let i = $index) {
                        <tr>
                          <td>{{ i + 1 }}</td>
                          <td>
                            <span class="d-inline-flex align-items-center gap-2">
                              <span>{{ charge.chargeType }}{{ charge.rateType ? ' (' + charge.rateType + ')' : '' }}</span>
                              @if (isApplicationFeeCharge(charge.chargeType) && suggestedApplicationMeta(); as meta) {
                                <span
                                  class="suggested-app-circle"
                                  [attr.title]="suggestedApplicationTooltip()"
                                >
                                  {{ meta.count }}
                                </span>
                              }
                            </span>
                          </td>
                          <td>{{ formatCurrency(charge.totalAmount) }}</td>
                          <td>{{ formatCurrency(charge.buyerAmount) }}</td>
                          <td>{{ formatCurrency(charge.sellerAmount) }}</td>
                          <td>{{ formatCurrency(charge.seciAmount) }}</td>
                        </tr>
                      }
                      <tr class="fw-semibold">
                        <td colspan="2">Total</td>
                        <td>{{ formatCurrency(contractChargeBreakdown().totalAmount) }}</td>
                        <td>{{ formatCurrency(contractChargeBreakdown().buyerAmount) }}</td>
                        <td>{{ formatCurrency(contractChargeBreakdown().sellerAmount) }}</td>
                        <td>{{ formatCurrency(contractChargeBreakdown().seciAmount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              }

              <div class="profit-table-shell mt-3">
                <table class="table profit-table mb-0">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>LOI</th>
                      <th>Type</th>
                      <th>Quantum</th>
                      <th>Rate</th>
                      <th>Rate Source</th>
                      <th>Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of estimatedProfitTableRows(); track item.rowId) {
                      <tr>
                        <td>{{ item.clientName }}</td>
                        <td>{{ item.loaNumber }}</td>
                        <td>{{ item.loiType }}</td>
                        <td [attr.title]="item.quantumTooltip || null">{{ item.quantumLabel || (formatNumber(item.quantum) + ' MWh') }}</td>
                        <td>{{ item.rateLabel || (item.rate === null ? 'NA' : formatCurrency(item.rate)) }}</td>
                        <td>{{ item.rateSource }}</td>
                        <td>
                          <span
                            class="profit-direction"
                            [class.revenue]="item.direction === 'revenue'"
                            [class.cost]="item.direction === 'cost'"
                            [class.excluded]="item.direction === 'excluded'"
                            [class.unknown]="item.direction === 'unknown'"
                          >
                            {{ formatEstimatedContribution(item) }}
                          </span>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        }
      </div>


      @if (chargeBreakdownModal(); as modal) {
        <div class="modal-backdrop-lite" (click)="closeChargeBreakdownModal()"></div>
        <div class="party-modal">
          <div class="party-modal-head d-flex align-items-center justify-content-between">
            <div>
              <h3 class="party-modal-title">{{ modal.title }}</h3>
              <div class="text-muted small">{{ modal.subtitle }}</div>
            </div>
            <button class="btn-close-square d-inline-flex align-items-center justify-content-center" type="button" (click)="closeChargeBreakdownModal()">
              <lucide-icon [img]="XIcon" [size]="16"></lucide-icon>
            </button>
          </div>
          <div class="party-modal-body">
            <div class="mb-3 fw-semibold">{{ modal.equation }}</div>
            <div class="text-muted small mb-3">{{ modal.hint }}</div>
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <tbody>
                  @for (detail of modal.details; track detail.label) {
                    <tr>
                      <td class="fw-semibold">{{ detail.label }}</td>
                      <td>{{ detail.value }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }

      @if (showAddModal()) {
        <div class="modal-backdrop-lite" (click)="closeAddModal()"></div>
        <div class="party-modal">
          <div class="party-modal-head d-flex align-items-center justify-content-between">
            <h3 class="party-modal-title">{{ editingRowId ? 'EDIT' : 'ADD' }}</h3>
            <button class="btn-close-square d-inline-flex align-items-center justify-content-center" type="button" (click)="closeAddModal()">
              <lucide-icon [img]="XIcon" [size]="16"></lucide-icon>
            </button>
          </div>
          <div class="party-modal-body">
            <div class="row g-3">
              <div class="col-12 col-lg-6">
                <div class="field-wrap">
                  <span class="field-label">Select Entity <span class="req">*</span></span>
                  <select class="form-select modal-select" [(ngModel)]="selectedEntityId" (ngModelChange)="onEntityChange()">
                    <option value="">Select Injector Entity Type</option>
                    @for (vendor of vendors(); track vendor.id) {
                      <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="col-12 col-lg-6">
                <div class="field-wrap">
                  <span class="field-label">Select LOI <span class="req">*</span></span>
                  <select class="form-select modal-select" [(ngModel)]="selectedLoiId" (ngModelChange)="onLoiChange()">
                    <option value="">Select Injector LOI Type</option>
                    @for (loa of approvedLoasForSelectedEntity(); track loa.id) {
                      <option [value]="loa.id">{{ loa.loa_number }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="col-12 col-lg-6">
                <div class="field-wrap">
                  <span class="field-label">Select Schedule Arrangement <span class="req">*</span></span>
                  <select class="form-select modal-select" [(ngModel)]="selectedScheduleAgreementId">
                    <option value="">Select Schedule Arrangement</option>
                    @for (schedule of scheduleOptions(); track schedule.id) {
                      <option [value]="schedule.id">{{ schedule.label }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="col-12 col-lg-6">
                <div class="field-wrap">
                  <span class="field-label">Select Agreement Type<span class="req">*</span></span>
                  <select class="form-select modal-select" [(ngModel)]="selectedAgreementType">
                    <option value="">Select Agreement Type</option>
                    <option value="NORMAL">NORMAL</option>
                    <option value="EXTENDED">EXTENDED</option>
                    <option value="REVISED">REVISED</option>
                  </select>
                </div>
              </div>
            @if (selectedAgreementType === 'EXTENDED' || selectedAgreementType === 'REVISED') {
                <div class="col-12 col-lg-6">
                  <div class="field-wrap">
                    <span class="field-label">Link LOI Type <span class="req">*</span></span>
                    <select class="form-select modal-select" [(ngModel)]="selectedLinkLoiType">
                      <option value="">Select Link LOI Type</option>
                      @for (loa of linkableLoasForSelectedEntity(); track loa.id) {
                        <option [value]="loa.loa_number">{{ loa.loa_number }}</option>
                      }
                    </select>
                  </div>
                </div>
              }
            </div>

            @if (linkedContractNumbers().length > 0) {
              <div class="linked-loa-alert mt-3">
                This LOI is linked with contract{{ linkedContractNumbers().length > 1 ? 's' : '' }}:
                {{ linkedContractNumbers().join(', ') }}
              </div>
            }

            <div class="d-flex justify-content-end mt-3">
              <button class="btn btn-party-add" type="button" [disabled]="!canAddPartyDetails()" (click)="submitAddPartyDetails()">
                {{ editingRowId ? 'PARTYDETAILS UPDATE' : 'PARTYDETAILS ADD' }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class ContractFormPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly contractsService = inject(ContractsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly loaService = inject(LoaService);
  private readonly chargesService = inject(ChargesService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  protected readonly isVendorUser = computed(() => this.auth.user()?.role === 'vendor');
  private readonly vendorId = computed(() => this.auth.user()?.vendor_id || '');

  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly PlusCircleIcon = PlusCircle;
  protected readonly XIcon = X;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly SendIcon = Send;

  protected readonly rows = signal<PartyRow[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly allLoas = signal<Loa[]>([]);
  protected readonly allContracts = signal<Contract[]>([]);
  protected readonly applications = signal<ApplicationApi[]>([]);
  protected readonly chargeMasters = signal<Charge[]>([]);
  protected readonly linkedContractNumbers = signal<string[]>([]);
  protected readonly loaSchedulesByLoa = signal<Record<string, LoaSchedule[]>>({});
  protected readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  protected readonly scheduleOptions = signal<ScheduleOption[]>([]);
  protected contractNumber = '';
  protected readonly mode = signal<Mode>('new');
  protected readonly contractId = signal<string | null>(null);
  protected readonly currentContract = signal<Contract | null>(null);
  protected readonly contractAnalysisRow = signal<ContractAnalysisReportRow | null>(null);
  protected readonly showAddModal = signal(false);
  protected readonly chargeBreakdownModal = signal<ChargeBreakdownModal | null>(null);
  protected readonly showCompatibility = computed(() => this.rows().length > 0);
  protected selectedEntityId = '';
  protected selectedLoiId = '';
  protected selectedScheduleAgreementId = '';
  protected selectedAgreementType = '';
  protected selectedLinkLoiType = '';
  protected editingRowId: number | null = null;
  protected readonly pageTitle = computed(() => {
    const base =
      this.mode() === 'edit'
        ? 'Edit Contract'
        : this.mode() === 'view'
          ? 'View Contract'
          : 'Add New Contract';
    return this.isBessEntry() ? `${base} - BESS` : base;
  });
  protected readonly isBessEntry = computed(() => (this.route.snapshot.queryParamMap.get('entityType') || '').toLowerCase() === 'bess');

  protected readonly compatibilityRows = computed<CompatibilityRow[]>(() => {
    const rows = this.compatibilityPartyRows();
    if (rows.length === 0) return [];
    const source = this.buildCompatibilityMetrics(rows[0]);
    const hasTarget = rows.length > 1;
    const target = hasTarget ? this.buildCompatibilityMetrics(rows[1]) : null;
    const statusFor = (left: string, right: string): 'MATCH' | 'MISMATCH' =>
      left.trim().toLowerCase() === right.trim().toLowerCase() ? 'MATCH' : 'MISMATCH';

    return [
      {
        parameter: 'Delivery Point',
        sourceValue: source.deliveryPoint,
        targetValue: target?.deliveryPoint || '-',
        status: hasTarget ? statusFor(source.deliveryPoint, target?.deliveryPoint || '') : 'MATCH',
      },
      {
        parameter: 'Quantum',
        sourceValue: source.quantum,
        targetValue: target?.quantum || '-',
        status: hasTarget ? statusFor(source.quantum, target?.quantum || '') : 'MATCH',
      },
      {
        parameter: 'Period',
        sourceValue: source.period,
        targetValue: target?.period || '-',
        status: hasTarget ? statusFor(source.period, target?.period || '') : 'MATCH',
      },
    ];
  });

  private readonly compatibilityPartyRows = computed(() => {
    return this.rows().filter((row, index, arr) => arr.findIndex((item) => item.loaId === row.loaId) === index).slice(0, 2);
  });
  protected readonly compatibilityHeaderSource = computed(() => this.compatibilityPartyRows()[0]?.clientLoi || 'LOI A');
  protected readonly compatibilityHeaderTarget = computed(() => this.compatibilityPartyRows()[1]?.clientLoi || '-');
  protected readonly hasCompatibilityTarget = computed(() => this.compatibilityPartyRows().length > 1);

  protected readonly chargeAllocationBlocks = computed(() => {
    const uniqueRows = this.rows().filter((row, index, arr) => arr.findIndex((item) => item.loaId === row.loaId) === index);
    const billConfigsByLoa = this.billConfigsByLoa();
    return uniqueRows
      .map((row) => {
        const configs = billConfigsByLoa[row.loaId] || [];
        const withOa = configs
          .filter((item) => !!item.oa_charge_allocation && String(item.oa_charge_allocation).trim() !== '')
          .sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''));
        if (!withOa.length) return null;
        const parsed = this.parseOaChargeAllocation(withOa[0].oa_charge_allocation);
        if (!parsed.length) return null;
        return {
          loaId: row.loaId,
          loaTitle: row.clientLoi,
          allocations: parsed,
        };
      })
      .filter((item): item is { loaId: string; loaTitle: string; allocations: OaChargeAllocation[] } => item !== null);
  });
  private readonly punchedChargeBreakdown = computed<ChargeBreakdownSummary>(() => {
    const contractId = String(this.currentContract()?.id || '').trim();
    if (!contractId) {
      return { totalAmount: 0, buyerAmount: 0, sellerAmount: 0, seciAmount: 0, rows: [] };
    }

    let totalAmount = 0;
    let buyerAmount = 0;
    let sellerAmount = 0;
    let seciAmount = 0;
    const rows: ChargeBreakdownRow[] = [];

    for (const application of this.applications()) {
      if (String(application.contract_id || '').trim() !== contractId) continue;
      const approvalCharges = application.approval_charges || [];
      if (!approvalCharges.length) continue;

      const allocationMap = this.oaChargeAllocationMap(application);

      for (const [index, charge] of approvalCharges.entries()) {
        const allocationKey = this.normalizeApplicationChargeType(charge, index);
        const master = this.resolveChargeMasterFromMasters(allocationKey);
        const rateType = String(master?.rate_type || '').trim() || null;
        const basis = this.resolveContractChargeBasis();
        const masterValue = master ? Number(master.value) : Number.NaN;
        const fallbackAmount = this.resolveApplicationChargeAmount(charge);
        const totalChargeAmount =
          master && Number.isFinite(masterValue) ? this.resolveChargeAmountFromRate(masterValue, rateType, basis) : fallbackAmount;
        const displayChargeName = String(master?.charge_name || '').trim() || allocationKey;
        const regionLabel = master?.charge_name || allocationKey;
        const requiredRegion = this.extractRegionCode(regionLabel);
        const regionalApplicable = this.shouldApplyRegionalCharge(regionLabel);
        if (requiredRegion && !regionalApplicable) continue;
        const effectiveTotal = totalChargeAmount;
        const allocation = allocationMap.get(allocationKey);
        const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
        const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
        const mappedPercent = buyerPercent + sellerPercent;
        const buyerShare = (effectiveTotal * buyerPercent) / 100;
        const sellerShare = (effectiveTotal * sellerPercent) / 100;
        const seciShare = Math.max(effectiveTotal - buyerShare - sellerShare, 0);

        totalAmount += effectiveTotal;
        buyerAmount += buyerShare;
        sellerAmount += sellerShare;
        seciAmount += seciShare;
        rows.push({
          applicationNumber: application.application_number || String(application.id),
          chargeType: displayChargeName,
          rateType,
          totalAmount: effectiveTotal,
          buyerAmount: buyerShare,
          sellerAmount: sellerShare,
          seciAmount: seciShare,
          allocationLabel: !allocation
            ? 'No buyer/seller mapping found, so the remaining amount is counted on SECI'
            : `Buyer ${this.formatNumber(buyerPercent)}% / Seller ${this.formatNumber(sellerPercent)}% / SECI ${this.formatNumber(Math.max(100 - mappedPercent, 0))}%`,
        });
      }
    }

    return {
      totalAmount,
      buyerAmount,
      sellerAmount,
      seciAmount,
      rows,
    };
  });

  private readonly configuredChargeEffectiveDate = computed(() => {
    const contract = this.currentContract();
    const value = String(contract?.period_from || contract?.issue_date || '').trim();
    return value || this.todayIsoDate();
  });

  private readonly configuredCharges = computed(() => {
    const cutoff = this.configuredChargeEffectiveDate();
    if (!cutoff) return [] as Charge[];

    const filtered = (this.chargeMasters() || [])
      .filter((row) => !!row && !!row.active)
      .filter((row) => String(row.effective_date || '').trim() !== '' && String(row.effective_date).trim() <= cutoff);

    const byKey = new Map<string, Charge>();
    for (const row of filtered) {
      const key = `${this.normalizeChargeGroup(row.charge_type)}|${String(row.charge_name || '').trim().toLowerCase()}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, row);
        continue;
      }
      if (String(row.effective_date || '') > String(existing.effective_date || '')) {
        byKey.set(key, row);
      }
    }

    return [...byKey.values()].sort((left, right) => {
      const groupCompare = this.normalizeChargeGroup(left.charge_type).localeCompare(this.normalizeChargeGroup(right.charge_type));
      if (groupCompare !== 0) return groupCompare;
      const nameCompare = String(left.charge_name || '').localeCompare(String(right.charge_name || ''));
      if (nameCompare !== 0) return nameCompare;
      return String(right.effective_date || '').localeCompare(String(left.effective_date || ''));
    });
  });

  private readonly configuredChargeBreakdown = computed<ChargeBreakdownSummary>(() => {
    const charges = this.configuredCharges();
    if (!charges.length) return { totalAmount: 0, buyerAmount: 0, sellerAmount: 0, seciAmount: 0, rows: [] };

    const loaIds = this.uniquePartyRows().map((row) => row.loaId).filter((id) => !!String(id || '').trim());
    if (!loaIds.length) return { totalAmount: 0, buyerAmount: 0, sellerAmount: 0, seciAmount: 0, rows: [] };

    const allocationMap = this.oaChargeAllocationMapFromLoaIds(loaIds);

    let totalAmount = 0;
    let buyerAmount = 0;
    let sellerAmount = 0;
    let seciAmount = 0;
    const rows: ChargeBreakdownRow[] = [];

    for (const charge of charges) {
      const baseValue = Number(charge.value);
      if (!Number.isFinite(baseValue) || baseValue <= 0) continue;
      const requiredRegion = this.extractRegionCode(charge.charge_name);
      const regionalApplicable = this.shouldApplyRegionalCharge(charge.charge_name);
      if (requiredRegion && !regionalApplicable) continue;

      const totalChargeAmount = this.resolveConfiguredChargeAmount(charge);
      if (!Number.isFinite(totalChargeAmount) || totalChargeAmount <= 0) continue;

      const allocationKey = this.normalizeChargeAllocationType(`${charge.charge_name || ''} ${charge.charge_type || ''}`.trim());
      const chargeName = String(charge.charge_name || '').trim();
      const displayChargeName = chargeName || allocationKey || 'Charge';
      const allocation = allocationMap.get(allocationKey);
      const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
      const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
      const mappedPercent = buyerPercent + sellerPercent;
      const buyerShare = (totalChargeAmount * buyerPercent) / 100;
      const sellerShare = (totalChargeAmount * sellerPercent) / 100;
      const seciShare = Math.max(totalChargeAmount - buyerShare - sellerShare, 0);

      totalAmount += totalChargeAmount;
      buyerAmount += buyerShare;
      sellerAmount += sellerShare;
      seciAmount += seciShare;
      rows.push({
        applicationNumber: String(charge.charge_type || '').trim() || 'Charge Master',
        chargeType: displayChargeName,
        rateType: charge.rate_type || null,
        totalAmount: totalChargeAmount,
        buyerAmount: buyerShare,
        sellerAmount: sellerShare,
        seciAmount: seciShare,
        allocationLabel: `Effective ${charge.effective_date} • Buyer ${this.formatNumber(buyerPercent)}% / Seller ${this.formatNumber(sellerPercent)}% / SECI ${this.formatNumber(Math.max(100 - mappedPercent, 0))}%`,
      });
    }

    return { totalAmount, buyerAmount, sellerAmount, seciAmount, rows };
  });

  protected readonly contractChargeBreakdown = computed<ChargeBreakdownSummary>(() =>
    buildContractChargeBreakdown({
      contractId: String(this.currentContract()?.id || '').trim(),
      cutoffDate: this.configuredChargeEffectiveDate(),
      periodFrom: String(this.currentContract()?.period_from || this.computeContractPeriod(this.uniquePartyRows()).from || '').trim(),
      periodTo: String(this.currentContract()?.period_to || this.computeContractPeriod(this.uniquePartyRows()).to || '').trim(),
      partyDetails: this.contractChargePartyDetails(),
      applications: this.applications(),
      chargeMasters: this.chargeMasters(),
      billConfigsByLoa: this.billConfigsByLoa(),
      loaSchedulesByLoa: this.loaSchedulesByLoa(),
    }),
  );
  protected readonly estimatedProfitRows = computed<EstimatedProfitRow[]>(() => {
    const rows = this.uniquePartyRows();
    const contractPeriod = this.computeContractPeriod(rows);
    const contractStart = this.parseDateOnly(contractPeriod.from);
    const contractEnd = this.parseDateOnly(contractPeriod.to);
    const estimatedRows: EstimatedProfitRow[] = rows.map((row): EstimatedProfitRow => {
      const loa = this.allLoas().find((item) => item.id === row.loaId);
      const loiType = (row.loiType || this.resolveLoiType(loa || ({ category: '' } as Loa))).trim() || '-';
      const rateDetails = this.resolveEstimatedRate(loa, this.billConfigsByLoa()[row.loaId] || []);
      const quantumMeta =
        contractStart && contractEnd
          ? this.resolveEstimatedContractUnitsMeta(contractStart, contractEnd, row.loaId, row.quantum)
          : {
              units: this.asNumber(row.quantum),
              tooltip: `Quantum fallback used\nLOA Quantum: ${this.formatNumber(this.asNumber(row.quantum))} MWh`,
            };
      const contractedUnits = quantumMeta.units;
      const quantum = contractedUnits > 0 ? contractedUnits : this.asNumber(row.quantum);
      const normalizedType = loiType.toLowerCase();

      if (normalizedType === 'sell') {
        return {
          rowId: row.id,
          clientName: row.clientName,
          loaNumber: row.clientLoi,
          loiType,
          quantum,
          rate: rateDetails.rate,
          amount: contractedUnits * (rateDetails.rate || 0),
          direction: rateDetails.rate === null ? 'unknown' : 'cost',
          rateSource: rateDetails.source,
          quantumTooltip: quantumMeta.tooltip,
        };
      }

      if (normalizedType === 'buy') {
        return {
          rowId: row.id,
          clientName: row.clientName,
          loaNumber: row.clientLoi,
          loiType,
          quantum,
          rate: rateDetails.rate,
          amount: contractedUnits * (rateDetails.rate || 0),
          direction: rateDetails.rate === null ? 'unknown' : 'revenue',
          rateSource: rateDetails.source,
          quantumTooltip: quantumMeta.tooltip,
        };
      }

      return {
        rowId: row.id,
        clientName: row.clientName,
        loaNumber: row.clientLoi,
        loiType,
        quantum,
        rate: rateDetails.rate,
        amount: 0,
        direction: 'excluded',
        rateSource: normalizedType === 'banking' ? 'Banking LOI excluded from profit estimate' : rateDetails.source,
        quantumTooltip: quantumMeta.tooltip,
      };
    });
    const chargeSummary = this.contractChargeBreakdown();
    let syntheticRowId = 100000;

    if (chargeSummary.buyerAmount > 0) {
      estimatedRows.push({
        rowId: syntheticRowId++,
        clientName: 'Buyer',
        loaNumber: 'Application Charges',
        loiType: 'Buyer Charge',
        quantum: 0,
        rate: null,
        amount: chargeSummary.buyerAmount,
        direction: 'cost',
        rateSource: 'Mapped buyer share from application_charges',
        quantumLabel: '-',
        quantumTooltip: 'Quantum not applicable for charge split rows',
        rateLabel: 'By charge split',
      });
    }

    if (chargeSummary.sellerAmount > 0) {
      estimatedRows.push({
        rowId: syntheticRowId++,
        clientName: 'Seller',
        loaNumber: 'Application Charges',
        loiType: 'Seller Charge',
        quantum: 0,
        rate: null,
        amount: chargeSummary.sellerAmount,
        direction: 'cost',
        rateSource: 'Mapped seller share from application_charges',
        quantumLabel: '-',
        quantumTooltip: 'Quantum not applicable for charge split rows',
        rateLabel: 'By charge split',
      });
    }

    if (chargeSummary.totalAmount > 0) {
      estimatedRows.push({
        rowId: syntheticRowId++,
        clientName: 'SECI',
        loaNumber: 'Application Charges',
        loiType: 'SECI Charge',
        quantum: 0,
        rate: null,
        amount: chargeSummary.seciAmount,
        direction: 'cost',
        rateSource: 'Unmapped application charge balance counted on SECI',
        quantumLabel: '-',
        quantumTooltip: 'Quantum not applicable for charge split rows',
        rateLabel: 'By charge split',
      });
    }

    return estimatedRows;
  });

  protected readonly estimatedProfitTableRows = computed(() =>
    this.estimatedProfitRows().filter((row) => (row.loaNumber || '').trim().toLowerCase() !== 'application charges'),
  );
  protected readonly estimatedRevenue = computed(() =>
    this.estimatedProfitRows()
      .filter((item) => item.direction === 'revenue')
      .reduce((acc, item) => acc + item.amount, 0),
  );
  protected readonly estimatedCost = computed(() =>
    this.estimatedProfitRows()
      .filter((item) => item.direction === 'cost')
      .reduce((acc, item) => acc + item.amount, 0),
  );
  protected readonly estimatedChargeCost = computed(() => this.contractChargeBreakdown().totalAmount);
  protected readonly estimatedProfit = computed(() => this.estimatedRevenue() - this.estimatedCost());
  private readonly estimatedCostWithoutCharges = computed(() => this.estimatedCost() - this.estimatedChargeCost());
  protected readonly estimatedSellerCost = computed(() => this.estimatedCostWithoutCharges());
  protected readonly estimatedBuyerCost = computed(() => this.estimatedRevenue());

  private readonly actualDeliveryRatio = computed<number | null>(() => {
    const row = this.contractAnalysisRow();
    if (!row) return null;
    const requestedMw = Number(row.requested_quantum_mw || 0);
    const approvedMw = Number(row.approved_quantum_mw || 0);
    if (!Number.isFinite(approvedMw) || approvedMw <= 0) return 0;
    if (Number.isFinite(requestedMw) && requestedMw > 0) return approvedMw / requestedMw;

    const scheduledMwh = Number(row.total_quantum_mwh || 0);
    const deliveredMwh = Number(row.delivered_mwh || 0);
    if (!Number.isFinite(scheduledMwh) || scheduledMwh <= 0) return 0;
    if (!Number.isFinite(deliveredMwh) || deliveredMwh <= 0) return 0;
    return deliveredMwh / scheduledMwh;
  });

  protected readonly actualRevenue = computed<number | null>(() => {
    const ratio = this.actualDeliveryRatio();
    if (ratio === null) return null;
    return this.estimatedRevenue() * ratio;
  });

  protected readonly actualCost = computed<number | null>(() => {
    const ratio = this.actualDeliveryRatio();
    if (ratio === null) return null;
    return this.estimatedCostWithoutCharges() * ratio;
  });

  protected readonly actualChargeBreakdown = computed<ChargeBreakdownSummary>(() => {
    const ratio = this.actualDeliveryRatio();
    const base = this.contractChargeBreakdown();
    if (ratio === null) return base;
    const safeRatio = Math.max(ratio, 0);
    if (safeRatio <= 0) {
      const rows = (base.rows || []).map((row) => ({
        ...row,
        totalAmount: 0,
        buyerAmount: 0,
        sellerAmount: 0,
        seciAmount: 0,
        allocationLabel: `${row.allocationLabel}${row.allocationLabel ? ' • ' : ''}Approved quantum is 0, so actual charge is 0`,
      }));
      return {
        totalAmount: 0,
        buyerAmount: 0,
        sellerAmount: 0,
        seciAmount: 0,
        rows,
      };
    }

    const rows = (base.rows || []).map((row) => {
      const shouldScaleByApprovedQuantum = String(row.rateType || '').trim() === 'Rate (₹/MWh)';
      if (!shouldScaleByApprovedQuantum) return { ...row };

      return {
        ...row,
        totalAmount: Number((row.totalAmount * safeRatio).toFixed(2)),
        buyerAmount: Number((row.buyerAmount * safeRatio).toFixed(2)),
        sellerAmount: Number((row.sellerAmount * safeRatio).toFixed(2)),
        seciAmount: Number((row.seciAmount * safeRatio).toFixed(2)),
        allocationLabel: `${row.allocationLabel}${row.allocationLabel ? ' • ' : ''}Approved quantum scaling ${this.formatNumber(safeRatio * 100)}%`,
      };
    });

    return {
      totalAmount: Number(rows.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2)),
      buyerAmount: Number(rows.reduce((sum, row) => sum + row.buyerAmount, 0).toFixed(2)),
      sellerAmount: Number(rows.reduce((sum, row) => sum + row.sellerAmount, 0).toFixed(2)),
      seciAmount: Number(rows.reduce((sum, row) => sum + row.seciAmount, 0).toFixed(2)),
      rows,
    };
  });

  protected readonly actualChargeCost = computed(() => this.actualChargeBreakdown().totalAmount);

  protected readonly actualProfit = computed<number | null>(() => {
    const backendActual = this.contractAnalysisRow()?.actual_profit;
    if (backendActual !== null && backendActual !== undefined) {
      return backendActual - this.actualChargeBreakdown().totalAmount;
    }
    const revenue = this.actualRevenue();
    const cost = this.actualCost();
    if (revenue === null || cost === null) return null;
    return revenue - cost - this.actualChargeBreakdown().totalAmount;
  });

  protected readonly actualProfitRows = computed<EstimatedProfitRow[]>(() => {
    const ratio = this.actualDeliveryRatio();
    if (ratio === null) return this.estimatedProfitRows();

    const chargeBreakdown = this.actualChargeBreakdown();
    return this.estimatedProfitRows().map((row) => {
      const isChargeRow = (row.loaNumber || '').trim().toLowerCase() === 'application charges';
      if (isChargeRow) {
        const client = (row.clientName || '').trim().toLowerCase();
        const amount =
          client === 'buyer'
            ? chargeBreakdown.buyerAmount
            : client === 'seller'
              ? chargeBreakdown.sellerAmount
              : client === 'seci'
                ? chargeBreakdown.seciAmount
                : row.amount;
        return {
          ...row,
          amount,
          rateSource: `${row.rateSource} (Approved scaling)`,
        };
      }

      if (row.direction === 'revenue' || row.direction === 'cost') {
        return {
          ...row,
          quantum: row.quantum * ratio,
          amount: row.amount * ratio,
          quantumTooltip: `${row.quantumTooltip || `Base Quantum: ${this.formatNumber(row.quantum)} MWh`}\nApproved scaling: ${this.formatNumber(ratio * 100)}%\nActual Quantum: ${this.formatNumber(row.quantum * ratio)} MWh`,
          rateSource: `${row.rateSource} (Approved scaling)`,
        };
      }
      return row;
    });
  });

  protected readonly actualProfitTableRows = computed(() =>
    this.actualProfitRows().filter((row) => (row.loaNumber || '').trim().toLowerCase() !== 'application charges'),
  );

  protected readonly suggestedApplicationMeta = computed<{
    count: number;
    billCycle: number;
    punchedCount: number;
    pendingCount: number;
    contractDays: number;
    contractPeriodLabel: string;
  } | null>(() => {

    const contract = this.currentContract();
    const contractPeriod = this.computeContractPeriod(this.uniquePartyRows());
    const contractStart = this.parseDateOnly(contract?.period_from || contractPeriod.from);
    const contractEnd = this.parseDateOnly(contract?.period_to || contractPeriod.to);
    if (!contractStart || !contractEnd) return null;

   const contractDays = this.inclusiveDayCount(contractStart, contractEnd);
    if (!Number.isFinite(contractDays) || contractDays <= 0) return null;

    // Auto decide bill cycle
    const isMonthly = contractDays >= 28;
    const billCycle = isMonthly ? 30 : 7;

    // Suggested applications
    const rawSuggested = contractDays / billCycle;
    if (!Number.isFinite(rawSuggested) || rawSuggested <= 0) return null;

    const count = Math.ceil(rawSuggested);
    const contractId = String(contract?.id || '').trim();
    const punchedCount = contractId
      ? this.applications().filter((application) => String(application.contract_id || '').trim() === contractId).length
      : 0;
    const pendingCount = Math.max(count - punchedCount, 0);

    return {
      count,
      billCycle,
      punchedCount,
      pendingCount,
      contractDays,
      contractPeriodLabel: `${String(contract?.period_from || contractPeriod.from || '').slice(0, 10)} - ${String(contract?.period_to || contractPeriod.to || '').slice(0, 10)}`,
    };
  });

  protected readonly suggestedApplicationTooltip = computed(() => {
    const meta = this.suggestedApplicationMeta();
    if (!meta) return '';
    return [
      `Min Suggested applications by end of contract: ${meta.count}`,
      `Already punched: ${meta.punchedCount}`,
      `Pending applications: ${meta.pendingCount}`,
      `Bill cycle (days): ${this.formatNumber(meta.billCycle)}`,
      `Contract days: ${this.formatNumber(meta.contractDays)}`,
      `Contract period: ${meta.contractPeriodLabel}`,
    ].join('\n');
  });

  protected readonly estimatedProfitTooltip = computed(() => {
    const buyer = this.estimatedBuyerCost();
    const seller = this.estimatedSellerCost();
    const charges = this.estimatedChargeCost();
    const net = this.estimatedProfit();
    return [
      'Net Estimated Profit calculation',
      `Buyer Cost: ${this.formatCurrency(buyer)}`,
      `Seller Cost: ${this.formatCurrency(seller)}`,
      `Application Charges: ${this.formatCurrency(charges)}`,
      `Formula: Buyer Cost - Seller Cost - Application Charges`,
      `${this.formatCurrency(buyer)} - ${this.formatCurrency(seller)} - ${this.formatCurrency(charges)} = ${this.formatCurrency(net)}`,
    ].join('\n');
  });

  protected readonly actualProfitTooltip = computed(() => {
    const ratio = this.actualDeliveryRatio();
    const buyer = this.actualRevenue();
    const seller = this.actualCost();
    const charges = this.actualChargeCost();
    const net = this.actualProfit();
    const backendActual = this.contractAnalysisRow()?.actual_profit;

    if (net === null || buyer === null || seller === null) {
      return 'Net Actual Profit calculation is not available because approved quantum or contract analysis values are missing.';
    }

    const lines = [
      'Net Actual Profit calculation',
      `Approved scaling: ${this.formatNumber((ratio || 0) * 100)}%`,
      `Buyer Cost: ${this.formatCurrency(buyer)}`,
      `Seller Cost: ${this.formatCurrency(seller)}`,
      `Application Charges: ${this.formatCurrency(charges)}`,
    ];

    if (backendActual !== null && backendActual !== undefined) {
      lines.push(`Base Actual Profit from Contracts Analysis: ${this.formatCurrency(backendActual)}`);
      lines.push('Formula: Base Actual Profit - Application Charges');
      lines.push(`${this.formatCurrency(backendActual)} - ${this.formatCurrency(charges)} = ${this.formatCurrency(net)}`);
      return lines.join('\n');
    }

    lines.push('Formula: Buyer Cost - Seller Cost - Application Charges');
    lines.push(`${this.formatCurrency(buyer)} - ${this.formatCurrency(seller)} - ${this.formatCurrency(charges)} = ${this.formatCurrency(net)}`);
    return lines.join('\n');
  });

  protected isApplicationFeeCharge(value: string | null | undefined): boolean {
    const text = String(value || '').trim().toUpperCase();
    if (!text) return false;
    return text.includes('APPLICATION') && text.includes('FEE');
  }

  private rowCounter = 0;

  constructor() {
    this.loadPartyDependencies();
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      const nextMode: Mode = id ? (this.router.url.includes('/edit') ? 'edit' : 'view') : 'new';
      if (this.isVendorUser() && !id) {
        this.toast.show('Vendor users can only view their own contracts', '', 'danger');
        this.router.navigateByUrl('/contracts');
        return;
      }
      this.mode.set(nextMode);
      this.contractId.set(id);
      this.currentContract.set(null);
      this.rows.set([]);
      if (id) {
        this.loadContract(id);
      } else {
        this.initializeContractNumber();
      }
    });
  }

  private loadPartyDependencies(): void {
    this.contractsService.list().subscribe({
      next: (contracts) => this.allContracts.set(contracts || []),
      error: () => this.allContracts.set([]),
    });
    this.vendorsService.list().subscribe({
      next: (vendors) => {
        this.vendors.set(
          [...vendors].sort((left, right) => {
            const leftRank = this.resolveEntityType(left) === 'BESS' ? 0 : 1;
            const rightRank = this.resolveEntityType(right) === 'BESS' ? 0 : 1;
            if (leftRank !== rightRank) return leftRank - rightRank;
            return left.vendor_name.localeCompare(right.vendor_name);
          }),
        );
        this.syncRowsFromCurrentContract();
      },
      error: () => this.vendors.set([]),
    });
    this.loaService.list().subscribe({
      next: (loas) => {
        this.allLoas.set(loas);
        this.syncRowsFromCurrentContract();
      },
      error: () => this.allLoas.set([]),
    });
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (applications) => {
        this.applications.set(applications || []);
        this.syncRowsFromCurrentContract();
      },
      error: () => this.applications.set([]),
    });
    this.chargesService.list().subscribe({
      next: (charges) => this.chargeMasters.set(charges || []),
      error: () => this.chargeMasters.set([]),
    });
  }

  private loadContract(id: string): void {
    this.contractsService.get(id).subscribe({
      next: (contract) => {
        if (!this.canViewContract(contract)) {
          this.toast.show('You can only view your own contracts', '', 'danger');
          this.router.navigateByUrl('/contracts');
          return;
        }
        if (this.isVendorUser() && this.mode() === 'edit') {
          this.toast.show('Vendor users can only view their own contracts', '', 'danger');
          this.router.navigateByUrl(`/contracts/${id}`);
          return;
        }
        this.currentContract.set(contract);
        this.contractNumber = contract.contract_number || '';
        this.syncRowsFromCurrentContract();
        if (this.mode() === 'view') {
          this.loadContractAnalysisRow(id);
        } else {
          this.contractAnalysisRow.set(null);
        }
        if (this.mode() === 'edit' && !this.canEditCurrentContract()) {
          this.toast.show('Only contract creator can edit pending contract', '', 'danger');
          this.router.navigateByUrl(`/contracts/${id}`);
        }
      },
      error: () => {
        this.toast.show('Contract not found', '', 'danger');
        this.router.navigateByUrl('/contracts');
      },
    });
  }

  private loadContractAnalysisRow(contractId: string): void {
    this.contractAnalysisRow.set(null);
    this.contractsService.analysisReport().subscribe({
      next: (payload) => {
        const match = (payload?.rows || []).find((row) => String(row.contract_id) === String(contractId)) || null;
        this.contractAnalysisRow.set(match);
      },
      error: () => this.contractAnalysisRow.set(null),
    });
  }

  private initializeContractNumber(): void {
    this.contractsService.list().subscribe({
      next: (contracts) => {
        this.contractNumber = this.buildNextContractNumber(contracts);
      },
      error: () => {
        this.contractNumber = this.buildFallbackContractNumber();
      },
    });
  }

  private buildNextContractNumber(contracts: Contract[]): string {
    const financialYear = this.currentFinancialYear();
    const prefix = `ST-125/BIL/${financialYear}/`;
    const maxSequence = contracts
      .map((item) => item.contract_number || '')
      .filter((number) => number.startsWith(prefix))
      .map((number) => number.split('/').pop() || '')
      .map((tail) => Number(tail))
      .filter((value) => Number.isFinite(value))
      .reduce((acc, value) => Math.max(acc, value), 0);

    return `${prefix}${String(maxSequence + 1).padStart(4, '0')}`;
  }

  private buildFallbackContractNumber(): string {
    const financialYear = this.currentFinancialYear();
    return `ST-125/BIL/${financialYear}/0001`;
  }

  private currentFinancialYear(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const startYear = month >= 4 ? year : year - 1;
    const endYearShort = String((startYear + 1) % 100).padStart(2, '0');
    return `${startYear}-${endYearShort}`;
  }

  protected openAddModal(): void {
    if (!this.canModifyRows()) return;
    this.editingRowId = null;
    this.selectedEntityId = '';
    this.selectedLoiId = '';
    this.selectedScheduleAgreementId = '';
    this.selectedAgreementType = '';
    this.selectedLinkLoiType = '';
    this.scheduleOptions.set([]);
    this.linkedContractNumbers.set([]);
    this.showAddModal.set(true);
  }

  protected closeAddModal(): void {
    this.editingRowId = null;
    this.linkedContractNumbers.set([]);
    this.showAddModal.set(false);
  }

  protected canAddPartyDetails(): boolean {
    if (!this.canModifyRows()) return false;
    const baseRequired = !!(this.selectedEntityId && this.selectedLoiId && this.selectedScheduleAgreementId && this.selectedAgreementType);
    if (!baseRequired) return false;
    const needsLinkLoiType = this.selectedAgreementType === 'EXTENDED' || this.selectedAgreementType === 'REVISED';
    return !needsLinkLoiType || !!this.selectedLinkLoiType;
  }

  protected onEntityChange(): void {
    this.selectedLoiId = '';
    this.selectedScheduleAgreementId = '';
    this.selectedAgreementType = '';
    this.selectedLinkLoiType = '';
    this.scheduleOptions.set([]);
    this.linkedContractNumbers.set([]);
  }

  protected onLoiChange(): void {
    this.selectedScheduleAgreementId = '';
    this.scheduleOptions.set([]);
    const loaId = this.selectedLoiId;
    if (!loaId) {
      this.linkedContractNumbers.set([]);
      return;
    }
    this.syncLinkedContractsForLoa(loaId);
    this.loadSchedulesForLoa(loaId);
  }

  private loadSchedulesForLoa(loaId: string, selectedScheduleId?: string): void {
    this.loaService.listSchedules(loaId).subscribe({
      next: (schedules) => {
        const options = schedules.map((item, index) => this.toScheduleOption(item, index));
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: schedules,
        });
        this.refreshRowsForLoaQuantum(loaId);
        this.scheduleOptions.set(options);
        if (selectedScheduleId && options.some((item) => item.id === selectedScheduleId)) {
          this.selectedScheduleAgreementId = selectedScheduleId;
        }
      },
      error: () => {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: [],
        });
        this.refreshRowsForLoaQuantum(loaId);
        this.scheduleOptions.set([]);
      },
    });
  }

  protected openEditModal(row: PartyRow): void {
    if (!this.canModifyRows()) return;
    this.editingRowId = row.id;
    this.selectedEntityId = row.clientId;
    this.selectedLoiId = row.loaId;
    this.selectedScheduleAgreementId = '';
    this.selectedAgreementType = row.agreementType;
    this.selectedLinkLoiType = row.linkLoi;
    this.scheduleOptions.set([]);
    this.syncLinkedContractsForLoa(row.loaId);
    if (row.loaId) {
      this.loadSchedulesForLoa(row.loaId, row.scheduleAgreementId);
    }
    this.showAddModal.set(true);
  }

  protected approvedLoasForSelectedEntity(): Loa[] {
    const entityId = this.selectedEntityId;
    if (!entityId) return [];
    return this.allLoas().filter((loa) => {
      if (loa.vendor_id !== entityId) return false;
      if (!this.isLoaApproved(loa)) return false;
      return this.isLoaTypeAllowedForSelection(loa);
    });
  }

  protected linkableLoasForSelectedEntity(): Loa[] {
    return this.approvedLoasForSelectedEntity().filter((loa) => loa.id !== this.selectedLoiId);
  }

  private syncLinkedContractsForLoa(loaId: string): void {
    const currentId = this.contractId();
    const linkedContracts = this.allContracts().filter((contract) => {
      if (currentId && contract.id === currentId) return false;
      return (contract.party_details || []).some((party) => party.loa_id === loaId);
    });
    const contractNumbers = Array.from(new Set(linkedContracts.map((contract) => contract.contract_number).filter((value) => !!value)));
    this.linkedContractNumbers.set(contractNumbers);
  }

  private toScheduleOption(item: LoaSchedule, index: number): ScheduleOption {
    const arrangement = item.arrangement ?? index + 1;
    const label = `Arrangement ${arrangement}`;
    const quantum = item.quantum_mw ? String(item.quantum_mw) : '0';
    return {
      id: item.id,
      label,
      quantum,
    };
  }

  private isLoaApproved(loa: Loa): boolean {
    const primaryStatus = (loa.loa_status || loa.status || '').toLowerCase().trim();
    const initialApproval = (loa.initial_approval_status || '').toLowerCase().trim();
    const accountApproval = (loa.account_approval_status || '').toLowerCase().trim();
    const finalApproval = (loa.final_approval_status || '').toLowerCase().trim();

    if (['approved', 'final_approved', 'active'].includes(primaryStatus)) return true;
    if (primaryStatus.endsWith('_approved')) return true;
    if ([initialApproval, accountApproval, finalApproval].some((value) => value === 'approved')) return true;

    return false;
  }

  protected submitAddPartyDetails(): void {
    if (!this.canAddPartyDetails()) return;
    const editingRow = this.rows().find((item) => item.id === this.editingRowId);
    const vendor = this.vendors().find((item) => item.id === this.selectedEntityId);
    const selectedLoa = this.allLoas().find((item) => item.id === this.selectedLoiId);
    const selectedSchedule = this.scheduleOptions().find((item) => item.id === this.selectedScheduleAgreementId);
    if (!vendor || !selectedLoa || !selectedSchedule) return;
    this.ensureSchedulesLoaded(selectedLoa.id);
    this.ensureBillConfigsLoaded(selectedLoa.id);
    const rowId = this.editingRowId ?? this.nextRowId();
    const next: PartyRow = {
      id: rowId,
      clientId: vendor.id,
      clientName: vendor.vendor_name || '',
      entityType: this.resolveEntityType(vendor),
      loaId: selectedLoa.id,
      clientLoi: selectedLoa.loa_number || '',
      scheduleAgreementId: selectedSchedule.id,
      loiType: this.resolveLoiType(selectedLoa),
      agreementType: this.selectedAgreementType,
      linkLoi:
        this.selectedAgreementType === 'EXTENDED' || this.selectedAgreementType === 'REVISED'
          ? this.selectedLinkLoiType
          : '',
      rldc: this.resolvePartyRldc(vendor, selectedLoa),
      quantum: selectedSchedule.quantum,
      quantum_mwh: this.quantumDisplayForLoa(selectedLoa.id, selectedSchedule.quantum),
      usedQuantum: editingRow?.usedQuantum || '0',
    };
    if (this.editingRowId) {
      this.rows.set(this.rows().map((item) => (item.id === this.editingRowId ? next : item)));
    } else {
      this.rows.set([...this.rows(), next]);
    }
    this.selectedEntityId = '';
    this.selectedLoiId = '';
    this.selectedScheduleAgreementId = '';
    this.selectedAgreementType = '';
    this.selectedLinkLoiType = '';
    this.editingRowId = null;
    this.scheduleOptions.set([]);
    this.linkedContractNumbers.set([]);
    this.showAddModal.set(false);
  }

  protected removeRow(id: number): void {
    if (!this.canModifyRows()) return;
    this.rows.set(this.rows().filter((row) => row.id !== id));
  }

  protected submitContract(): void {
    if (this.mode() === 'view') return;
    if (this.isVendorUser()) {
      this.toast.show('Vendor users cannot add or edit contracts', '', 'danger');
      return;
    }
    if (this.mode() === 'edit' && !this.canEditCurrentContract()) {
      this.toast.show('Only contract creator can edit pending contract', '', 'danger');
      return;
    }
    const uniqueRows = this.uniquePartyRows();
    const uniqueLoaCount = uniqueRows.length;
    if (uniqueLoaCount < 2) {
      this.toast.show('Minimum two LOA required', '', 'danger');
      return;
    }
    const contractType = this.computeContractType(uniqueRows);
    const contractCategory = this.computeContractCategory(uniqueRows);
    const period = this.computeContractPeriod(uniqueRows);
    const allMatched = this.compatibilityRows().every((item) => item.status === 'MATCH');
    const compatibilityStatus = allMatched ? 'Matched' : 'Mismatched';
    const status = allMatched ? 'approved' : 'pending_accounts_approval';
    const issueDate = this.todayIsoDate();

    const payload: Partial<Contract> = {
        contract_number: this.contractNumber,
        contract_type: contractType,
        contract_category: contractCategory,
        issue_date: issueDate,
        period_from: period.from,
        period_to: period.to,
        compatibility_status: compatibilityStatus,
        status,
        party_details: uniqueRows.map((row) => ({
          vendor_id: row.clientId,
          loa_id: row.loaId,
          quantum: this.asNumber(row.quantum),
          agreement_type: row.agreementType || null,
          party_type: row.loiType || null,
          entity_type: row.entityType || null,
          rldc: row.rldc || this.resolvePartyRldc(this.vendors().find((vendor) => vendor.id === row.clientId), this.allLoas().find((loa) => loa.id === row.loaId)) || null,
        })),
      };

    const req$ = this.mode() === 'edit' && this.contractId() ? this.contractsService.update(this.contractId()!, payload) : this.contractsService.create(payload);
    req$.subscribe({
        next: () => {
          this.toast.show(
            allMatched
              ? 'Contract saved and auto-approved'
              : 'Contract saved and sent for Accounts/Operations approval',
            '',
            'success',
          );
          this.router.navigateByUrl('/contracts');
        },
        error: (error: unknown) => {
          const message = this.extractContractSaveError(error);
          this.toast.show(message, '', 'danger');
        },
      });
  }

  protected canModifyRows(): boolean {
    if (this.isVendorUser()) return false;
    if (this.mode() === 'view') return false;
    if (this.mode() === 'edit') return this.canEditCurrentContract();
    return true;
  }

  protected canEditCurrentContract(): boolean {
    if (this.isVendorUser()) return false;
    const contract = this.currentContract();
    if (!contract) return this.mode() === 'new';
    const userId = this.auth.user()?.id || '';
    const isCreator = (contract.created_by || '') === userId;
    const isPending = (contract.status || '').toLowerCase().includes('pending');
    return isCreator && isPending;
  }

  protected canApproveCurrentContract(): boolean {
    if (this.mode() !== 'view') return false;
    const contract = this.currentContract();
    if (!contract) return false;
    const isCreator = (contract.created_by || '') === (this.auth.user()?.id || '');
    if (isCreator) return false;
    return !!(contract.can_accounts_approve || contract.can_operation_approve || contract.can_approve);
  }

  protected goToEdit(): void {
    const id = this.contractId();
    if (!id) return;
    if (this.isVendorUser()) {
      this.toast.show('Vendor users can only view their own contracts', '', 'danger');
      return;
    }
    this.router.navigateByUrl(`/contracts/${id}/edit`);
  }

  private canViewContract(contract: Contract): boolean {
    if (!this.isVendorUser()) return true;
    const vendorId = this.vendorId();
    if (!vendorId) return false;
    return (contract.party_details || []).some((party) => (party.vendor_id || '') === vendorId);
  }

  protected approveContract(): void {
    const contract = this.currentContract();
    const id = this.contractId();
    if (!contract || !id) return;
    const req$ = contract.can_accounts_approve
      ? this.contractsService.accountsApprove(id)
      : this.contractsService.operationApprove(id);
    req$.subscribe({
      next: (updated) => {
        this.currentContract.set(updated);
        this.toast.show('Contract approved', '', 'success');
      },
      error: () => this.toast.show('Failed to approve contract', '', 'danger'),
    });
  }

  protected rejectContract(): void {
    const contract = this.currentContract();
    const id = this.contractId();
    if (!contract || !id) return;
    const remarks = prompt('Enter rejection remarks');
    if (remarks === null) return;
    if (!remarks.trim()) {
      this.toast.show('Rejection remarks are required', '', 'danger');
      return;
    }
    const req$ = contract.can_accounts_approve
      ? this.contractsService.accountsReject(id, remarks.trim())
      : this.contractsService.operationReject(id, remarks.trim());
    req$.subscribe({
      next: (updated) => {
        this.currentContract.set(updated);
        this.toast.show('Contract rejected', '', 'success');
      },
      error: () => this.toast.show('Failed to reject contract', '', 'danger'),
    });
  }

  private ensureBillConfigsLoaded(loaId: string): void {
    if (!loaId) return;
    if (this.billConfigsByLoa()[loaId]) return;
    this.loaService.listBillConfigs(loaId).subscribe({
      next: (items) => {
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: items,
        });
      },
      error: () => {
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: [],
        });
      },
    });
  }

  private ensureSchedulesLoaded(loaId: string): void {
    if (!loaId) return;
    if (this.loaSchedulesByLoa()[loaId]) return;
    this.loaService.listSchedules(loaId).subscribe({
      next: (items) => {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: items,
        });
        this.refreshRowsForLoaQuantum(loaId);
      },
      error: () => {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: [],
        });
        this.refreshRowsForLoaQuantum(loaId);
      },
    });
  }

  private buildCompatibilityMetrics(row: PartyRow): { deliveryPoint: string; quantum: string; period: string } {
    const loa = this.allLoas().find((item) => item.id === row.loaId);
    const schedules = this.loaSchedulesByLoa()[row.loaId] || [];
    return {
      deliveryPoint: (loa?.delivery_point || '-').trim() || '-',
      quantum: this.buildQuantumDisplay(loa, schedules, row.quantum),
      period: this.buildPeriodDisplay(schedules),
    };
  }

  private buildQuantumDisplay(loa: Loa | undefined, schedules: LoaSchedule[], fallbackQuantum: string): string {
    const baseQuantum = loa?.id ? this.totalQuantumForLoa(loa.id, fallbackQuantum) : this.asNumber(fallbackQuantum);
    const loaType = this.resolveLoiType(loa || ({ category: '' } as Loa)).toLowerCase();
    if (loaType === 'banking') {
      const returnQuantum = baseQuantum * (this.maximumReturnPercentage(schedules) / 100);
      const returnPercentage = baseQuantum > 0 ? (returnQuantum / baseQuantum) * 100 : 0;
      const allQuantum = baseQuantum + returnQuantum;
      return `${this.formatNumber(baseQuantum)} MWh | Return ${this.formatNumber(returnPercentage)}% | All ${this.formatNumber(allQuantum)} MWh`;
    }
    return `${this.formatNumber(baseQuantum)} MWh`;
  }

  private maximumReturnPercentage(schedules: LoaSchedule[]): number {
    return schedules.reduce((maxPercent, item) => Math.max(maxPercent, this.asNumber(item.return_percentage)), 0);
  }

  private totalQuantumForLoa(loaId: string, fallbackQuantum?: string | number | null): number {
    const schedules = this.loaSchedulesByLoa()[loaId] || [];
    const totalQuantum = schedules.reduce((total, schedule) => {
      const scheduleStart = this.parseDateOnly(schedule.from_date);
      const scheduleEnd = this.parseDateOnly(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const dayDifference = this.inclusiveDayCount(scheduleStart, scheduleEnd);
      if (!dayDifference) return total;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      const quantum = this.asNumber(schedule.quantum_mw);
      if (!Number.isFinite(quantum) || quantum <= 0) return total;

      return total + dayDifference * timeDifference * quantum * 250;
    }, 0);

    return totalQuantum > 0 ? totalQuantum : this.asNumber(fallbackQuantum);
  }

  private quantumDisplayForLoa(loaId: string, fallbackQuantum?: string | number | null): string {
    return this.formatNumber(this.totalQuantumForLoa(loaId, fallbackQuantum));
  }

  private refreshRowsForLoaQuantum(loaId: string): void {
    const currentRows = this.rows();
    const nextRows = currentRows.map((row) =>
      row.loaId === loaId ? { ...row, quantum_mwh: this.quantumDisplayForLoa(loaId, row.quantum) } : row,
    );
    const changed = nextRows.some((row, index) => row.quantum !== currentRows[index]?.quantum);
    if (changed) {
      this.rows.set(nextRows);
    }
  }

  private buildPeriodDisplay(schedules: LoaSchedule[]): string {
    const periods = schedules
      .map((item) => ({ from: (item.from_date || '').trim(), to: (item.to_date || '').trim() }))
      .filter((item) => item.from && item.to);
    if (!periods.length) return '-';
    const minFrom = periods.reduce((acc, item) => (item.from < acc ? item.from : acc), periods[0].from);
    const maxTo = periods.reduce((acc, item) => (item.to > acc ? item.to : acc), periods[0].to);
    return `${minFrom} - ${maxTo}`;
  }

  private asNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  protected formatNumber(value: number): string {
    return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
  }

  protected formatCurrency(value: number): string {
    return `Rs ${this.formatNumber(value)}`;
  }

  protected formatCurrencyMaybe(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'NA';
    return this.formatCurrency(value);
  }

  protected isActualProfitPositive(): boolean {
    const value = this.actualProfit();
    return value !== null && value >= 0;
  }

  protected isActualProfitNegative(): boolean {
    const value = this.actualProfit();
    return value !== null && value < 0;
  }

  protected formatEstimatedContribution(item: EstimatedProfitRow): string {
    if (item.direction === 'excluded') return 'Excluded';
    if (item.direction === 'unknown') return 'Rate not available';
    const prefix = item.direction === 'revenue' ? '+' : '-';
    return `${prefix}${this.formatCurrency(item.amount)}`;
  }

  protected openChargeBreakdownModal(): void {
    const summary = this.contractChargeBreakdown();
    const usesChargeMasters = this.configuredChargeBreakdown().rows.length > 0;
    const cutoff = this.configuredChargeEffectiveDate();
    const details: Array<{ label: string; value: string }> = [
      { label: 'Total Application Charges', value: this.formatCurrency(summary.totalAmount) },
      { label: 'Buyer Share', value: this.formatCurrency(summary.buyerAmount) },
      { label: 'Seller Share', value: this.formatCurrency(summary.sellerAmount) },
      { label: 'SECI Share', value: this.formatCurrency(summary.seciAmount) },
    ];

    for (const row of summary.rows) {
      const chargeLabel = `${row.chargeType}${row.rateType ? ' (' + row.rateType + ')' : ''}`;
      details.push({
        label: `${row.applicationNumber} • ${chargeLabel}`,
        value: `${this.formatCurrency(row.totalAmount)} | Buyer ${this.formatCurrency(row.buyerAmount)} | Seller ${this.formatCurrency(row.sellerAmount)} | SECI ${this.formatCurrency(row.seciAmount)} | ${row.allocationLabel}`,
      });
    }

    this.chargeBreakdownModal.set({
      title: 'Application Charge Bifurcation',
      subtitle: this.contractNumber || this.currentContract()?.contract_number || 'Contract',
      equation: `${this.formatCurrency(summary.totalAmount)} = Buyer ${this.formatCurrency(summary.buyerAmount)} + Seller ${this.formatCurrency(summary.sellerAmount)} + SECI ${this.formatCurrency(summary.seciAmount)}`,
      hint: summary.rows.length
        ? usesChargeMasters
          ? `Charges are picked from Manage Charges (effective on/before ${cutoff}). Mapped buyer and seller shares are added as charge rows in Estimated Profit. Any unmapped remainder is counted as a SECI charge and deducted from profit.`
          : 'Charges are picked from punched application charges. Mapped buyer and seller shares are added as charge rows in Estimated Profit. Any unmapped remainder is counted as a SECI charge and deducted from profit.'
        : usesChargeMasters
          ? `No active charge masters found on/before ${cutoff}.`
          : 'No punched application charges were found for this contract.',
      details,
    });
  }

  protected openActualChargeBreakdownModal(): void {
    const summary = this.actualChargeBreakdown();
    const details: Array<{ label: string; value: string }> = [
      { label: 'Total Application Charges', value: this.formatCurrency(summary.totalAmount) },
      { label: 'Buyer Share', value: this.formatCurrency(summary.buyerAmount) },
      { label: 'Seller Share', value: this.formatCurrency(summary.sellerAmount) },
      { label: 'SECI Share', value: this.formatCurrency(summary.seciAmount) },
    ];

    for (const row of summary.rows) {
      const chargeLabel = `${row.chargeType}${row.rateType ? ' (' + row.rateType + ')' : ''}`;
      details.push({
        label: `${row.applicationNumber} • ${chargeLabel}`,
        value: `${this.formatCurrency(row.totalAmount)} | Buyer ${this.formatCurrency(row.buyerAmount)} | Seller ${this.formatCurrency(row.sellerAmount)} | SECI ${this.formatCurrency(row.seciAmount)} | ${row.allocationLabel}`,
      });
    }

    this.chargeBreakdownModal.set({
      title: 'Application Charge Bifurcation (Actual Profit)',
      subtitle: this.contractNumber || this.currentContract()?.contract_number || 'Contract',
      equation: `${this.formatCurrency(summary.totalAmount)} = Buyer ${this.formatCurrency(summary.buyerAmount)} + Seller ${this.formatCurrency(summary.sellerAmount)} + SECI ${this.formatCurrency(summary.seciAmount)}`,
      hint: summary.rows.length
        ? 'Charges are picked from Manage Charges (effective on/before contract start) when available, otherwise from punched application charges. Mapped buyer and seller shares are added as charge rows in Actual Profit. Any unmapped remainder is counted as a SECI charge and deducted from profit.'
        : 'No punched application charges were found for this contract.',
      details,
    });
  }

  protected closeChargeBreakdownModal(): void {
    this.chargeBreakdownModal.set(null);
  }

  private parseOaChargeAllocation(raw: string | null | undefined): OaChargeAllocation[] {
    if (!raw || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const charge_type = this.normalizeChargeAllocationType(String((item as Partial<OaChargeAllocation>).charge_type || '').trim());
          if (!charge_type) return null;
          const payerInput = String((item as Partial<OaChargeAllocation>).payer || 'Shared');
          const payer: OaChargeAllocation['payer'] =
            payerInput === 'Buyer' || payerInput === 'Seller' || payerInput === 'Shared' ? payerInput : 'Shared';
          const buyer = Number((item as Partial<OaChargeAllocation>).buyer_percent);
          const seller = Number((item as Partial<OaChargeAllocation>).seller_percent);
          return {
            charge_type,
            payer,
            buyer_percent: Number.isFinite(buyer) ? buyer : 0,
            seller_percent: Number.isFinite(seller) ? seller : 0,
          };
        })
        .filter((item): item is OaChargeAllocation => item !== null);
    } catch {
      return [];
    }
  }

  private latestOaChargeAllocation(loaId: string): string | null {
    if (!loaId) return null;
    const configs = this.billConfigsByLoa()[loaId] || [];
    const latest = [...configs]
      .filter((item) => (item.bill_type || '').trim().toUpperCase() === 'OA')
      .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''))[0];
    return latest?.oa_charge_allocation || null;
  }

  private oaChargeAllocationMapFromLoaIds(loaIds: string[]): Map<string, OaChargeAllocation> {
    const uniqueLoaIds = (loaIds || []).map((id) => String(id || '').trim()).filter((id) => !!id);
    const map = new Map<string, OaChargeAllocation>();
    for (const loaId of uniqueLoaIds) {
      const items = this.parseOaChargeAllocation(this.latestOaChargeAllocation(loaId));
      for (const item of items) {
        if (!map.has(item.charge_type)) {
          map.set(item.charge_type, item);
        }
      }
    }
    return map;
  }

  private oaChargeAllocationMap(application: ApplicationApi): Map<string, OaChargeAllocation> {
    const loaIds = [String(application.seller_loa_id || '').trim(), String(application.buyer_loa_id || '').trim()].filter((id) => !!id);
    return this.oaChargeAllocationMapFromLoaIds(loaIds);
  }

  private normalizeChargeGroup(value: string | null | undefined): string {
    return String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
  }

  private extractRegionCode(value: string | null | undefined): string | null {
    const text = String(value || '').trim().toUpperCase();
    if (!text) return null;
    const explicit = /\b(ERLDC|NERLDC|NRLDC|SRLDC|WRLDC)\b/.exec(text);
    if (explicit) return explicit[1];

    // Accept common region labels stored in `party_details.rldc`.
    if (/\bNORTH\s*EAST\b/.test(text) || /\bNORTHEAST\b/.test(text)) return 'NERLDC';
    if (/\bEAST\b/.test(text) || /\bEASTERN\b/.test(text)) return 'ERLDC';
    if (/\bNORTH\b/.test(text) || /\bNORTHERN\b/.test(text)) return 'NRLDC';
    if (/\bSOUTH\b/.test(text) || /\bSOUTHERN\b/.test(text)) return 'SRLDC';
    if (/\bWEST\b/.test(text) || /\bWESTERN\b/.test(text)) return 'WRLDC';

    return null;
  }

  private readonly contractRegionCodesSet = computed(() => {
    const set = new Set<string>();
    for (const party of this.contractChargePartyDetails()) {
      const code = this.extractRegionCode(party?.rldc);
      if (code) set.add(code);
    }
    return set;
  });

  private contractChargePartyDetails(): ContractChargePartyLike[] {
    const contract = this.currentContract();
    if (contract?.party_details?.length) {
      return contract.party_details.map((party) => ({
        loaId: String(party.loa_id || '').trim(),
        quantum: party.quantum ?? null,
        rldc: party.rldc || null,
      }));
    }

    return this.uniquePartyRows().map((row) => ({
      loaId: String(row.loaId || '').trim(),
      quantum: row.quantum,
      rldc: row.rldc || null,
    }));
  }

  private shouldApplyRegionalCharge(chargeLabel: string | null | undefined): boolean {
    const required = this.extractRegionCode(chargeLabel);
    if (!required) return true;
    return this.contractRegionCodesSet().has(required);
  }

  private resolveConfiguredChargeAmount(charge: Charge): number {
    const value = Number(charge.value);
    if (!Number.isFinite(value) || value < 0) return 0;
    if (!this.shouldApplyRegionalCharge(charge.charge_name)) return 0;
    const basis = this.resolveContractChargeBasis();
    const computed = this.resolveChargeAmountFromRate(value, charge.rate_type, basis);
    return Number((Number.isFinite(computed) ? computed : 0).toFixed(2));
  }

  private normalizeChargeAllocationType(value: string): string {
    const normalized = value.trim().toUpperCase();
    if (!normalized) return '';
    if (normalized.includes('TRANSMISSION')) return 'Transmission';
    if (normalized.includes('WHEEL')) return 'Wheeling';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'Loss';
    if (normalized.includes('APP')) return 'App';
    return value.trim();
  }

  private resolveChargeMasterFromMasters(chargeLabel: string): Charge | null {
    const needle = String(chargeLabel || '').trim().toUpperCase();
    if (!needle) return null;

    const cutoff = this.configuredChargeEffectiveDate();
    if (!cutoff) return null;

    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const containsNeedle = new RegExp(`\\b${escaped}\\b`, 'i');

    let best: Charge | null = null;
    for (const row of this.chargeMasters() || []) {
      if (!row || !row.active) continue;
      const effective = String(row.effective_date || '').trim();
      if (!effective || effective > cutoff) continue;

      const group = String(row.charge_type || '').trim();
      const name = String(row.charge_name || '').trim();
      if (!group && !name) continue;

      const matchesGroup = !!group && (group.trim().toUpperCase() === needle || containsNeedle.test(group));
      const matchesName = !!name && (name.trim().toUpperCase() === needle || containsNeedle.test(name));
      if (!matchesGroup && !matchesName) continue;

      if (!best || String(best.effective_date || '') < effective) {
        best = row;
      }
    }

    return best;
  }

  private resolveContractChargeBasis(): { days: number; energyMwh: number } {
    const rows = this.uniquePartyRows();
    const contractPeriod = this.computeContractPeriod(rows);
    const contractStart = this.parseDateOnly(contractPeriod.from);
    const contractEnd = this.parseDateOnly(contractPeriod.to);
    if (!contractStart || !contractEnd) return { days: 0, energyMwh: 0 };

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);

    const energyByLoa = new Map<string, number>();
    for (const row of rows) {
      const loaId = String(row.loaId || '').trim();
      if (!loaId || energyByLoa.has(loaId)) continue;

      const schedules = this.loaSchedulesByLoa()[loaId] || [];
      const scheduleEnergy = schedules.reduce((total, schedule) => {
        const scheduleStart = this.parseDateOnly(schedule.from_date);
        const scheduleEnd = this.parseDateOnly(schedule.to_date);
        if (!scheduleStart || !scheduleEnd) return total;

        const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
        if (!overlapDays) return total;

        const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
        if (!timeDifference) return total;

        const quantum = this.firstFiniteNumber(schedule.quantum_mw, row.quantum);
        if (quantum === null) return total;

        return total + overlapDays * timeDifference * quantum;
      }, 0);

      if (scheduleEnergy > 0) {
        energyByLoa.set(loaId, scheduleEnergy);
        continue;
      }

      const quantum = this.firstFiniteNumber(row.quantum);
      energyByLoa.set(loaId, quantum === null ? 0 : daysDifference * 24 * quantum);
    }

    const energyMwh = energyByLoa.size > 0 ? Math.max(...energyByLoa.values()) : 0;
    return { days: daysDifference, energyMwh };
  }

  private resolveChargeAmountFromRate(value: number, rateType: string | null | undefined, basis: { days: number; energyMwh: number }): number {
    if (!Number.isFinite(value) || value < 0) return 0;
    const normalized = String(rateType || '').trim();
    if (normalized === 'Rate (₹/MWh)') return value * basis.energyMwh;
    if (normalized === 'Rate (₹/Day)') return value * basis.days;
    return value;
  }

  private normalizeApplicationChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    if (!raw) return `Charge ${index + 1}`;
    return this.normalizeChargeAllocationType(raw);
  }

  private resolveApplicationChargeAmount(item: ApplicationChargeApi): number {
    const amount = Number(item.amount);
    if (Number.isFinite(amount) && amount !== 0) return amount;
    return this.asNumber(item.rate_mwh) * this.asNumber(item.quantum_mwh);
  }

  private sanitizePercent(value: number | null | undefined): number {
    if (!Number.isFinite(Number(value))) return 0;
    return Math.min(Math.max(Number(value), 0), 100);
  }

  private cappedSellerPercent(value: number | null | undefined, buyerPercent: number): number {
    return Math.min(this.sanitizePercent(value), Math.max(100 - buyerPercent, 0));
  }

  private nextRowId(): number {
    this.rowCounter += 1;
    return this.rowCounter;
  }

  private resolveLoiType(loa: Loa): string {
    const value = (loa.category || '').trim().toLowerCase();
    if (!value) return '-';
    if (value.includes('bank')) return 'Banking';
    if (value.includes('buy') || value.includes('purchase')) return 'Buy';
    if (value.includes('sell') || value.includes('sale')) return 'Sell';
    return loa.category;
  }

  private resolveEstimatedRate(loa: Loa | undefined, billConfigs: LoaBillConfig[]): { rate: number | null; source: string } {
    const datedConfigs = [...billConfigs].sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''));
    const preferredConfigs = datedConfigs.filter((item) => (item.bill_type || '').trim().toUpperCase() !== 'OA');

    for (const config of preferredConfigs.length ? preferredConfigs : datedConfigs) {
      const directRate = this.firstFiniteNumber(config.rate, config.tmr_rate, config.mcp_rate);
      if (directRate !== null) {
        const billType = (config.bill_type || 'Bill').trim().toUpperCase();
        const effectiveDate = config.effective_date ? ` (${config.effective_date})` : '';
        return { rate: directRate, source: `${billType} bill config${effectiveDate}` };
      }
    }

    const loaRate = this.firstFiniteNumber(loa?.fixed_rate, loa?.mcp_value);
    if (loaRate !== null) {
      return { rate: loaRate, source: loa?.fixed_rate ? 'LOI fixed rate' : 'LOI MCP value' };
    }

    return { rate: null, source: 'Rate not available' };
  }

  private firstFiniteNumber(...values: Array<string | number | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.asNumber(value);
      if (value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed !== 0) {
        return parsed;
      }
    }
    return null;
  }

  private resolveEntityType(vendor: Vendor | undefined): string {
    const generatorType = (vendor?.generator_type || '').trim().toLowerCase();
    const vendorName = (vendor?.vendor_name || '').trim().toLowerCase();
    if (generatorType === 'battery' || vendorName.startsWith('bess-')) return 'BESS';
    return 'VENDOR';
  }

  private isLoaTypeAllowedForSelection(candidate: Loa): boolean {
    const candidateType = this.resolveLoiType(candidate).toLowerCase();
    const rows = this.rows();
    if (rows.length === 0) return true;
    const baseRow = rows.find((row) => row.id !== this.editingRowId) || rows[0];
    const baseType = (baseRow?.loiType || '').toLowerCase();
    if (!baseType) return true;
    if (baseType === 'buy') return candidateType === 'sell' || candidateType === 'banking';
    if (baseType === 'sell') return candidateType === 'buy' || candidateType === 'banking';
    if (baseType === 'banking') return candidateType === 'banking';
    return true;
  }

  private uniquePartyRows(): PartyRow[] {
    return this.rows().filter((row, index, arr) => arr.findIndex((item) => item.loaId === row.loaId) === index);
  }

  private computeContractType(rows: PartyRow[]): string {
    const rank = (term: string): number => {
      const normalized = term.trim().toUpperCase();
      if (normalized.includes('LTOA')) return 3;
      if (normalized.includes('MTOA')) return 2;
      if (normalized.includes('STOA')) return 1;
      return 0;
    };
    const label = (value: number): string => {
      if (value >= 3) return 'LTOA';
      if (value >= 2) return 'MTOA';
      return 'STOA';
    };
    const maxRank = rows.reduce((acc, row) => {
      const loa = this.allLoas().find((item) => item.id === row.loaId);
      return Math.max(acc, rank(loa?.loa_term || ''));
    }, 0);
    return label(maxRank);
  }

  private computeContractCategory(rows: PartyRow[]): string {
    const types = rows.map((row) => row.loiType.toLowerCase());
    const hasBanking = types.some((item) => item === 'banking');
    const hasBuyOrSell = types.some((item) => item === 'buy' || item === 'sell');
    if (hasBanking && hasBuyOrSell) return 'HYBRID';
    if (hasBanking && !hasBuyOrSell) return 'BANKING';
    return 'SALE-PURCHASE';
  }

  private computeContractPeriod(rows: PartyRow[]): { from: string; to: string } {
    const schedulePeriods = rows
      .flatMap((row) =>
        (this.loaSchedulesByLoa()[row.loaId] || [])
          .map((item) => ({ from: (item.from_date || '').trim(), to: (item.to_date || '').trim() }))
          .filter((item) => item.from && item.to),
      );
    if (schedulePeriods.length > 0) {
      const from = schedulePeriods.reduce((acc, item) => (item.from < acc ? item.from : acc), schedulePeriods[0].from);
      const to = schedulePeriods.reduce((acc, item) => (item.to > acc ? item.to : acc), schedulePeriods[0].to);
      return { from, to };
    }

    const loaPeriods = rows
      .map((row) => this.allLoas().find((item) => item.id === row.loaId))
      .filter((item): item is Loa => !!item)
      .map((loa) => ({
        from: (loa.effective_from || loa.issue_date || '').trim(),
        to: (loa.effective_to || loa.effective_from || loa.issue_date || '').trim(),
      }))
      .filter((item) => item.from && item.to);
    if (loaPeriods.length > 0) {
      const from = loaPeriods.reduce((acc, item) => (item.from < acc ? item.from : acc), loaPeriods[0].from);
      const to = loaPeriods.reduce((acc, item) => (item.to > acc ? item.to : acc), loaPeriods[0].to);
      return { from, to };
    }

    const today = this.todayIsoDate();
    return { from: today, to: today };
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = value.trim().split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private resolveEstimatedContractUnitsMeta(
    contractStart: Date,
    contractEnd: Date,
    loaId: string,
    fallbackQuantum: string | number | null | undefined,
  ): { units: number; tooltip: string } {
    const schedules = this.loaSchedulesByLoa()[loaId] || [];
    const lines = [
      'Quantum calculation',
      `Contract period: ${this.formatDateOnly(contractStart)} to ${this.formatDateOnly(contractEnd)}`,
    ];
    let scheduleUnits = 0;

    schedules.forEach((schedule, index) => {
      const scheduleStart = this.parseDateOnly(schedule.from_date);
      const scheduleEnd = this.parseDateOnly(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return;

      const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
      if (!overlapDays) return;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return;

      const quantum = this.firstFiniteNumber(schedule.quantum_mw, fallbackQuantum);
      if (quantum === null) return;

      const units = overlapDays * timeDifference * quantum * 250;
      scheduleUnits += units;
      lines.push(
        `Schedule ${index + 1}: ${this.formatDateOnly(scheduleStart)} to ${this.formatDateOnly(scheduleEnd)} | ${schedule.from_time || '-'} to ${schedule.to_time || '-'} | Days ${this.formatNumber(overlapDays)} | Hours ${this.formatNumber(timeDifference)} | Quantum ${this.formatNumber(quantum)} | MWh ${this.formatNumber(units)}`,
      );
    });

    if (scheduleUnits > 0) {
      lines.push(`Formula: Days x Hours x Quantum x 250`);
      lines.push(`Total Quantum: ${this.formatNumber(scheduleUnits)} MWh`);
      return { units: scheduleUnits, tooltip: lines.join('\n') };
    }

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);
    const quantum = this.firstFiniteNumber(fallbackQuantum);
    if (!daysDifference || quantum === null) {
      lines.push('No valid schedules or fallback quantum found');
      return { units: 0, tooltip: lines.join('\n') };
    }

    const units = daysDifference * 24 * quantum * 250;
    lines.push('No valid overlapping schedules found, so fallback quantum is used');
    lines.push(`Fallback Days: ${this.formatNumber(daysDifference)}`);
    lines.push(`Fallback Hours/Day: 24`);
    lines.push(`Fallback Quantum: ${this.formatNumber(quantum)}`);
    lines.push(`Formula: Days x 24 x Quantum x 250`);
    lines.push(`Total Quantum: ${this.formatNumber(units)} MWh`);
    return { units, tooltip: lines.join('\n') };
  }

  private formatDateOnly(value: Date): string {
    return value.toISOString().slice(0, 10);
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private overlapDayCount(rangeStart: Date, rangeEnd: Date, scheduleStart: Date, scheduleEnd: Date): number {
    const start = Math.max(rangeStart.getTime(), scheduleStart.getTime());
    const end = Math.min(rangeEnd.getTime(), scheduleEnd.getTime());
    if (end < start) return 0;
    return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime);
    if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
    if (hours === 24 && minutes === 0) return 24 * 60;
    if (hours < 0 || hours > 23) return null;
    return hours * 60 + minutes;
  }

  private parseBillCycle(value: string | null | undefined): number | null {
    const text = String(value || '').trim();
    if (!text) return null;
    const numeric = Number(text);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;

    const normalized = text.toLowerCase();
    if (normalized.includes('daily')) return 1;
    if (normalized.includes('weekly')) return 7;
    if (normalized.includes('fortnight')) return 15;
    if (normalized.includes('monthly')) return 30;
    return null;
  }

  private billCycleValue(): number | null {
    const loaIds = this.uniquePartyRows().map((row) => String(row.loaId || '').trim()).filter((id) => !!id);
    const configsByLoa = this.billConfigsByLoa();
    const configs = loaIds.flatMap((loaId) => configsByLoa[loaId] || []);
    if (!configs.length) return null;

    const sorted = [...configs].sort((a, b) => String(b.effective_date || '').localeCompare(String(a.effective_date || '')));
    for (const config of sorted) {
      const parsed = this.parseBillCycle(config.bill_cycle);
      if (parsed !== null) return parsed;
    }
    return null;
  }

  private todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private resolvePartyRldc(vendor: Vendor | undefined, loa: Loa | undefined): string | null {
    const text = [
      vendor?.region,
      vendor?.state,
      loa?.regional_type,
      loa?.state,
      loa?.delivery_point,
      loa?.remarks,
    ]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value)
      .join(' ');
    return this.extractRegionCode(text);
  }

  private syncRowsFromCurrentContract(): void {
    const contract = this.currentContract();
    if (!contract || !Array.isArray(contract.party_details)) return;
    const mapped = contract.party_details.map((item, index) => {
      const vendor = this.vendors().find((v) => v.id === item.vendor_id);
      const loa = this.allLoas().find((l) => l.id === item.loa_id);
      const loaType = loa ? this.resolveLoiType(loa) : '-';
      if (item.loa_id) {
        this.ensureSchedulesLoaded(item.loa_id);
        this.ensureBillConfigsLoaded(item.loa_id);
      }
      return {
        id: index + 1,
        clientId: item.vendor_id,
        clientName: vendor?.vendor_name || '',
        entityType: item.entity_type || this.resolveEntityType(vendor),
        loaId: item.loa_id,
        clientLoi: loa?.loa_number || item.loa_id,
        scheduleAgreementId: '',
        loiType: loaType,
        agreementType: item.agreement_type || 'NORMAL',
        linkLoi: '',
        rldc: item.rldc || this.resolvePartyRldc(vendor, loa),
        quantum: Number(item.quantum).toFixed(3),
        quantum_mwh: this.quantumDisplayForLoa(item.loa_id, item.quantum),
        usedQuantum: this.formatNumber(this.calculateUsedQuantumForLoa(item.loa_id)),
      } as PartyRow;
    });
    this.rowCounter = mapped.length;
    this.rows.set(mapped);
  }

  private calculateUsedQuantumForLoa(loaId: string | null | undefined): number {
    const normalizedLoaId = String(loaId || '').trim();
    const contractId = String(this.currentContract()?.id || '').trim();
    if (!normalizedLoaId || !contractId) return 0;

    return this.applications().reduce((total, application) => {
      if (String(application.contract_id || '').trim() !== contractId) return total;
      const sellerLoaId = String(application.seller_loa_id || '').trim();
      const buyerLoaId = String(application.buyer_loa_id || '').trim();
      if (sellerLoaId !== normalizedLoaId && buyerLoaId !== normalizedLoaId) return total;

      return total + this.calculateUsedQuantumFromSchedules(application.schedules || []);
    }, 0);
  }

  private calculateUsedQuantumFromSchedules(
    schedules: Array<{
      from_date: string;
      to_date: string;
      from_time: string;
      to_time: string;
      quantum?: number | string | null;
    }>,
  ): number {
    return schedules.reduce((total, schedule) => {
      const scheduleStart = this.parseDateOnly(schedule.from_date);
      const scheduleEnd = this.parseDateOnly(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const dayDifference = this.inclusiveDayCount(scheduleStart, scheduleEnd);
      if (!dayDifference) return total;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      const quantum = this.asNumber(schedule.quantum);
      if (!Number.isFinite(quantum) || quantum <= 0) return total;

      return total + dayDifference * timeDifference * quantum * 250;
    }, 0);
  }

  private extractContractSaveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'Unable to reach backend API. Check FastAPI server, API base URL, and CORS settings.';
      }
      const detail = this.extractApiDetail(error.error);
      const text = `${detail || error.message || ''}`.toLowerCase();
      if (text.includes('contract_category') && text.includes('does not exist')) {
        return 'DB migration pending: apply YoYo migration 20260308_08_contract_workflow_fields';
      }
      if (text.includes('duplicate key') && text.includes('contract_number')) {
        return 'Contract number already exists. Refresh and try again.';
      }
      if (text.includes('contract loa mapping cannot be changed after applications are linked')) {
        return 'This contract already has linked applications, so its LOA mapping cannot be changed.';
      }
      if (detail) return `Failed to save contract: ${detail}`;
    }
    return 'Failed to save contract';
  }

  private extractApiDetail(payload: unknown): string {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (payload instanceof ProgressEvent) return '';
    if (typeof payload === 'object') {
      const data = payload as Record<string, unknown>;
      const detail = data['detail'];
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        const first = detail[0] as Record<string, unknown> | undefined;
        if (first && typeof first['msg'] === 'string') return first['msg'];
      }
      if (typeof data['message'] === 'string') return data['message'];
      try {
        return JSON.stringify(payload);
      } catch {
        return '';
      }
    }
    return '';
  }
}
